/**
 * Caderno de Campo Pedagógico página editorial assimétrica em papel mineral,
 * tinta azul-marinho e acentos Vermelho Caderno. Leitura clara antes de decoração.
 */
import { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import {
  ArrowDownToLine,
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileDown,
  FileText,
  Filter,
  GraduationCap,
  History,
  Info,
  LockKeyhole,
  Menu,
  Medal,
  Pause,
  Play,
  Printer,
  RotateCcw,
  Search,
  Timer,
  Trophy,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StudentAuthDialog } from "@/components/StudentAuthDialog";
import { questions, areaSummary, type Question } from "@/data/simulado";
import { matematicaEnemVisualLocalMap } from "@/data/matematica-enem-visual-local";
import { matematicaEnemVisualTextMap } from "@/data/matematica-enem-visual-text";
import { matematicaEnemVisualReview72 } from "@/data/matematica-enem-visual-review-72";
import { useAuth } from "@/_core/hooks/useAuth";
import { loadRemoteProgress, loadRemoteAttempts, saveRemoteProgress, saveRemoteAttempt } from "@/lib/progressApi";
import { createClassroom, joinClassroom, loadMyClassrooms, type Classroom } from "@/lib/classroomApi";
import { loadStudentAccessType, saveStudentAccessType, saveStudentDisplayName, type StudentAccessType } from "@/lib/studentAccessApi";
import { loadTeacherStudents, type TeacherStudent } from "@/lib/teacherApi";
import { hasInstitutionalTeacherAccess } from "@shared/identityRoles";
import { cadernoPdfFilename, paginateCadernoForPrint, selectCadernoPrintBlocks, type CadernoPrintBlock } from "@shared/cadernoPrint";
import { calculateSimulationScore } from "@shared/simulationScoring";
import { ATTEMPT_QUESTION_COUNT, QUESTIONS_PER_AREA_PER_ATTEMPT, buildAttemptQuestions } from "@shared/attemptQuestionSelection";
import { MAX_DAILY_ATTEMPTS, getDailyAttemptCycle, isCurrentLocalDay } from "@shared/dailyAttemptCycle";

type AreaName = (typeof questions)[number]["area"];
type FilterArea = AreaName | "Todas";
type DownloadMode = "caderno" | "gabarito" | "mascara";
type CadernoOutputMode = "print" | "pdf";
type AttemptArea = { short: string; correct: number; answered: number; blank: number; percentage: number };
type Attempt = {
  id: string;
  studentKey: string;
  studentName: string;
  classroomKey: string;
  classroomId?: string | null;
  classroom: string;
  createdAt: string;
  correct: number;
  answered: number;
  percentage: number;
  remainingSeconds: number;
  byArea: AttemptArea[];
  answers: Record<number, string>;
  questionIds?: Record<number, string>;
};

const areaMeta: Record<AreaName, { short: string; color: string; pale: string; bar: string; index: string }> = {
  "Linguagens, Códigos e suas Tecnologias": { short: "Linguagens", color: "#C84D3A", pale: "#F5E1D9", bar: "#C84D3A", index: "01" },
  "Ciências Humanas e suas Tecnologias": { short: "Humanas", color: "#8A6B2D", pale: "#F1E8CC", bar: "#B28A3A", index: "02" },
  "Ciências da Natureza e suas Tecnologias": { short: "Natureza", color: "#497464", pale: "#DDEBE5", bar: "#5D8C78", index: "03" },
  "Matemática e suas Tecnologias": { short: "Matemática", color: "#1D4C72", pale: "#DDE8F1", bar: "#3B709D", index: "04" },
};

function getAreaMeta(area: string) {
  const normalized = area.trim().normalize("NFC");

  return areaMeta[normalized as AreaName] ?? {
    short: area || "Área",
    color: "#64748B",
    pale: "#F1F5F9",
    bar: "#64748B",
    index: "--",
  };
}
const chartColors = ["#C84D3A", "#B28A3A", "#5D8C78", "#3B709D"];
const operationData = [
  { name: "Leitura e argumentação", value: 32 },
  { name: "Modelagem e cálculo", value: 25 },
  { name: "Análise de fenômenos", value: 25 },
  { name: "Contexto histórico-social", value: 18 },
];

type TimerPreset = "dia1" | "dia2";

const STUDENT_PROFILE_OPTIONS = [
  { id: "3a", label: "3A", description: "Turma 3A", code: "3A2026" },
  { id: "3b", label: "3B", description: "Turma 3B", code: "3B2026" },
  { id: "elite", label: "Alunos Elite", description: "Turma Alunos Elite", code: "ELITE26" },
  { id: "independent", label: "Aluno Avulso", description: "Sem vínculo com turma", code: null },
] as const;

type StudentProfileId = typeof STUDENT_PROFILE_OPTIONS[number]["id"];

const timerPresets: Record<TimerPreset, { label: string; seconds: number }> = {
  dia1: { label: "1.º dia · 5h30", seconds: 5 * 60 * 60 + 30 * 60 },
  dia2: { label: "2.º dia · 5h", seconds: 5 * 60 * 60 },
};

const ATTEMPTS_STORAGE_KEY = "simulado-enem-attempts-v1";
const PROFILE_STORAGE_KEY = "simulado-enem-profile-v1";
const PROGRESS_STORAGE_KEY = "simulado-enem-progress-v2";
const attemptAreaSummary = areaSummary.map((entry) => ({ ...entry, count: QUESTIONS_PER_AREA_PER_ATTEMPT }));

function getVisualMathReference(question: Question) {
  const originalNumber = question.numeroOriginal;
  if (!originalNumber) return null;
  const imageUrl = matematicaEnemVisualLocalMap[originalNumber];
  if (!imageUrl) return null;
  return { imageUrl };
}

function getVisualMathText(question: Question) {
  const originalNumber = question.numeroOriginal;
  if (!originalNumber) return null;
  const extracted = matematicaEnemVisualTextMap[originalNumber];
  if (extracted) return extracted;
  const review = matematicaEnemVisualReview72.find((item) => item.numeroOriginal === originalNumber);
  return review
    ? { enunciado: review.enunciado, alternativas: {} }
    : null;
}

const CADERNO_PRINT_BLOCKS: Array<CadernoPrintBlock & { label: string; description: string }> = [
  { id: "linguagens", startQuestion: 1, endQuestion: 25, label: "Linguagens", description: "Questões 01–25 · 25 itens da área" },
  { id: "humanas", startQuestion: 26, endQuestion: 50, label: "Ciências Humanas", description: "Questões 26–50 · 25 itens da área" },
  { id: "natureza", startQuestion: 51, endQuestion: 75, label: "Ciências da Natureza", description: "Questões 51–75 · 25 itens da área" },
  { id: "matematica", startQuestion: 76, endQuestion: 100, label: "Matemática", description: "Questões 76–100 · 25 itens da área" },
];

function normalizeIdentity(value: string, fallback: string) {
  return (value.trim() || fallback)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/\s+/g, " ");
}

function initialAttempts(): Attempt[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = JSON.parse(window.localStorage.getItem(ATTEMPTS_STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

function initialProfile() {
  const emptyProfile = { studentEmail: "", studentName: "", classroom: "", localId: "servidor" };
  if (typeof window === "undefined") return emptyProfile;
  try {
    const stored = JSON.parse(window.localStorage.getItem(PROFILE_STORAGE_KEY) || "{}");
    return { studentEmail: typeof stored.studentEmail === "string" ? stored.studentEmail : "", studentName: typeof stored.studentName === "string" ? stored.studentName : "", classroom: typeof stored.classroom === "string" ? stored.classroom : "", localId: typeof stored.localId === "string" ? stored.localId : `perfil-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
  } catch {
    return { ...emptyProfile, localId: `perfil-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
  }
}

function formatDuration(totalSeconds: number) {
  const safeSeconds = Math.max(0, totalSeconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function csvCell(value: string | number) {
  return `"${String(value).replace(/"/g, '""')}"`;
}

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}
function markdownFor(mode: DownloadMode, sourceQuestions: readonly Question[] = questions) {
  const documentTitle = {
    caderno: "SIMULADO ENEM - Caderno de Questões",
    gabarito: "SIMULADO ENEM - Gabarito Comentado",
    mascara: "SIMULADO ENEM - Folha de Respostas e Máscara de Correção",
  }[mode];
  const header = `# ${documentTitle}\n\nEscola: ________________________________________________________________\nProfessor(a): ___________________________________________________________\nEstudante: _____________________________________________________________\nTurma: ____________________    Data: ____ / ____ / ______\n\n`;

  if (mode === "caderno") {
    return header + `> Material autoral reformulado, elaborado a partir de habilidades, temas e estruturas recorrentes em provas oficiais do ENEM.\n\n` + sourceQuestions.map((q) => `## Questão ${String(q.numero).padStart(2, "0")} - ${q.areaCurta}\n\n${q.enunciado}\n\nA. ${q.alternativas.A}\n\nB. ${q.alternativas.B}\n\nC. ${q.alternativas.C}\n\nD. ${q.alternativas.D}\n`).join("\n---\n\n");
  }
  if (mode === "gabarito") {
    return header + sourceQuestions.map((q) => `| ${String(q.numero).padStart(3, "0")} | **${q.correta}** | ${q.habilidade} | ${q.justificativa} |`).join("\n").replace(/^/, "| Questão | Resposta | Habilidade | Justificativa |\n| ---: | :---: | --- | --- |\n");
  }
  return header + `## Cartão-resposta\n\n| Questão | A | B | C | D |\n| ---: | :---: | :---: | :---: | :---: |\n${sourceQuestions.map((q) => `| ${String(q.numero).padStart(3, "0")} | ○ | ○ | ○ | ○ |`).join("\n")}\n\n---\n\n## Chave de correção\n\n| Questão | Resposta |\n| ---: | :---: |\n${sourceQuestions.map((q) => `| ${String(q.numero).padStart(3, "0")} | **${q.correta}** |`).join("\n")}`;
}

function downloadFile(mode: DownloadMode, sourceQuestions: readonly Question[] = questions) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = 18;
  const ensure = (height = 12) => {
    if (y > pageHeight - height) {
      doc.addPage();
      y = 18;
    }
  };
  const title = mode === "gabarito" ? "SIMULADO ENEM - GABARITO" : "SIMULADO ENEM - MÁSCARA DE CORREÇÃO";
  doc.setFillColor(29, 42, 68);
  doc.rect(0, 0, pageWidth, 28, "F");
  doc.setTextColor(255, 250, 242);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, 15, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Material para aplicação e correção pedagógica", 15, 21);
  y = 38;
  doc.setTextColor(29, 42, 68);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Escola: ________________________________________________", 15, y);
  doc.text("Professor(a): ___________________________________________", 15, y + 6);
  doc.text("Estudante: ______________________________________________", 15, y + 12);
  doc.text("Turma: __________________________ Data: ____ / ____ / ______", 15, y + 18);
  y += 30;

  if (mode === "mascara") {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Cartão-resposta", 15, y);
    y += 7;
    sourceQuestions.forEach((question, index) => {
      ensure(9);
      const x = 15 + (index % 4) * 45;
      const rowY = y + Math.floor(index / 4) * 10;
      if (index > 0 && index % 4 === 0) y += 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(String(question.numero).padStart(2, "0"), x, rowY);
      ["A", "B", "C", "D"].forEach((letter, letterIndex) => {
        doc.circle(x + 12 + letterIndex * 7, rowY - 1, 2.2);
        doc.setFont("helvetica", "normal");
        doc.text(letter, x + 10.8 + letterIndex * 7, rowY + 0.5);
      });
    });
    y += Math.ceil(sourceQuestions.length / 4) * 10 + 8;
    ensure(20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Chave de correção", 15, y);
    y += 7;
    sourceQuestions.forEach((question, index) => {
      ensure(7);
      const x = 15 + (index % 8) * 22;
      const rowY = y + Math.floor(index / 8) * 7;
      if (index > 0 && index % 8 === 0) y += 7;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(String(question.numero).padStart(2, "0") + " " + question.correta, x, rowY);
    });
    doc.save("simulado-enem-mascara-correcao.pdf");
    return;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Chave de correção e habilidade", 15, y);
  y += 8;
  sourceQuestions.forEach((question) => {
    ensure(15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(String(question.numero).padStart(2, "0") + " · " + question.correta, 15, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const detail = question.habilidade + " — " + question.justificativa;
    const lines = doc.splitTextToSize(detail, pageWidth - 42);
    doc.text(lines, 35, y);
    y += Math.max(7, lines.length * 3.2);
  });
  doc.save("simulado-enem-gabarito.pdf");
}

function escapeHtml(value: string) {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] || character);
}

function printableCadernoHtml(selectedQuestions: readonly Question[], selectionLabel: string) {
  const pages = paginateCadernoForPrint(selectedQuestions);
  return pages.map((page, pageIndex) => {
    const questionHtml = page.map((question) => `
      <article class="print-question">
        <div class="print-question-meta"><strong>Questão ${String(question.numero).padStart(2, "0")}</strong><span>${escapeHtml(question.areaCurta)}</span></div>
        <p class="print-stem">${escapeHtml(question.enunciado)}</p>
        <ol class="print-alternatives" type="A">
          ${(["A", "B", "C", "D"] as const).map((letter) => `<li>${escapeHtml(formatMathSymbols(question.alternativas[letter]))}</li>`).join("")}
        </ol>
      </article>`).join("");
    const identification = pageIndex === 0 ? `<div class="print-identification"><span>Escola: ______________________________________________</span><span>Professor(a): ________________________________________</span><span>Estudante: __________________________________________</span><span>Turma: ____________________ Data: ____ / ____ / ______</span></div>` : "";
    return `<section class="print-page"><header class="print-page-header"><span>SIMULADO ENEM INTERATIVO</span><strong>Caderno de questões</strong><span>Folha ${String(pageIndex + 1).padStart(2, "0")} de ${String(pages.length).padStart(2, "0")}</span></header><p class="print-selection">${escapeHtml(selectionLabel)}</p>${identification}${questionHtml}<footer class="print-page-footer"><span>Material autoral reformulado para aplicação pedagógica.</span><span>${pageIndex + 1} / ${pages.length}</span></footer></section>`;
  }).join("");
}

function printPreview(selectedQuestions: readonly Question[], selectionLabel: string) {
  if (!selectedQuestions.length) return false;
  const printWindow = window.open("", "_blank", "noopener,noreferrer");
  if (!printWindow) return false;
  printWindow.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>Simulado ENEM - Caderno</title><style>@page{size:A4;margin:12mm 14mm}*{box-sizing:border-box}body{margin:0;background:#eef0f2;color:#182940;font-family:Arial,sans-serif}.print-page{position:relative;min-height:273mm;overflow:hidden;background:#fff;padding:0 0 15mm;page-break-after:always;break-after:page}.print-page:last-child{page-break-after:auto;break-after:auto}.print-page-header{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:9mm;border-top:3px solid #c84d3a;border-bottom:1px solid #cdd4db;padding:3mm 0 3.4mm;color:#546477;font-size:7.5pt;font-weight:700;letter-spacing:.08em;text-transform:uppercase}.print-page-header strong{color:#1d2a44;font-size:9pt;letter-spacing:.025em;text-transform:none}.print-page-header span:last-child{text-align:right}.print-selection{margin:3mm 0 0;color:#6a7888;font-size:7.5pt;font-weight:700;letter-spacing:.05em;text-transform:uppercase}.print-identification{display:grid;grid-template-columns:1fr 1fr;gap:3.3mm 8mm;margin:5mm 0 4mm;border:1px solid #d6dce0;padding:3.5mm 4mm;color:#3f5066;font-size:8.2pt;line-height:1.25}.print-question{break-inside:avoid;page-break-inside:avoid;padding:4.5mm 0 4.3mm;border-bottom:1px solid #dce1e5}.print-question-meta{display:flex;justify-content:space-between;gap:7mm;color:#6a7888;font-size:7.5pt;text-transform:uppercase;letter-spacing:.075em}.print-question-meta strong{color:#c84d3a;font-size:9.4pt;letter-spacing:.03em}.print-stem{margin:2.2mm 0 2.6mm;color:#1d2a44;font-size:9.8pt;line-height:1.4}.print-alternatives{display:grid;grid-template-columns:1fr 1fr;gap:1.6mm 8mm;margin:0;padding-left:5mm;color:#33455a;font-size:8.9pt;line-height:1.35}.print-alternatives li{padding-left:1mm}.print-page-footer{position:absolute;right:0;bottom:0;left:0;display:flex;justify-content:space-between;border-top:1px solid #d6dce0;padding-top:2.5mm;color:#718092;font-size:7.2pt}@media screen{body{padding:16px}.print-page{width:182mm;min-height:273mm;margin:0 auto 16px;padding:12mm 14mm 15mm;box-shadow:0 5px 18px rgba(22,37,55,.16)}}@media print{body{background:#fff}.print-page{width:auto;margin:0;padding:0 0 15mm;box-shadow:none}}</style></head><body>${printableCadernoHtml(selectedQuestions, selectionLabel)}</body></html>`);
  printWindow.document.close();
  printWindow.focus();
  window.setTimeout(() => printWindow.print(), 300);
  return true;
}
function StudentAccessGate({ onLogin }: { onLogin: () => void }) {
  return (
    <div
      className="student-access-gate"
      role="region"
      aria-labelledby="student-access-title"
    >
      <div className="student-access-icon">
        <UserRound size={22} />
      </div>

      <div className="student-access-copy">
        <span className="mini-label">ACESSO DO ALUNO</span>

        <h3 id="student-access-title">
          Entre para iniciar o simulado.
        </h3>

        <p>
          Conecte sua conta Google para salvar seu progresso,
          suas tentativas e seu histórico.
        </p>
      </div>

      <button
        type="button"
        className="timer-start"
        onClick={onLogin}
      >
        Entrar com Google
        <ArrowRight size={15} />
      </button>
    </div>
  );
}

function formatMathSymbols(value: string) {
  const superscriptMap: Record<string, string> = {
    "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴",
    "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹",
    "+": "⁺", "-": "⁻", "(": "⁽", ")": "⁾",
  };
  const toSuperscript = (text: string) => text.split("").map((char) => superscriptMap[char] ?? char).join("");

  return value
    .replace(/\\sqrt\\s*\\{\\s*([^{}]+?)\\s*\\}/gi, "√$1")
    .replace(/\\sqrt\\s*\\(\\s*([^()]+?)\\s*\\)/gi, "√$1")
    .replace(/\\sqrt\\s+([A-Za-z0-9.,]+)/gi, "√$1")
    .replace(/\\bsqrt\\s*\\(\\s*([^()]+?)\\s*\\)/gi, "√$1")
    .replace(/\\bsqrt\\s*([A-Za-z0-9.,]+)/gi, "√$1")
    .replace(/\\times|\\cdot/gi, "×")
    .replace(/\\div/gi, "÷")
    .replace(/\\pi/gi, "π")
    .replace(/\\leq?|<=/gi, "≤")
    .replace(/\\geq?|>=/gi, "≥")
    .replace(/\\neq|!=/gi, "≠")
    .replace(/\\pm/gi, "±")
    .replace(/\\approx/gi, "≈")
    .replace(/\\infty/gi, "∞")
    .replace(/\\to/gi, "→")
    .replace(/\\circ/gi, "°")
    .replace(/\\^\\s*\\{([^{}]+)\\}/g, (_, exponent) => toSuperscript(exponent))
    .replace(/\\^\\s*([0-9]+)/g, (_, exponent) => toSuperscript(exponent))
    .replace(/([A-Za-z0-9)])\\s*\\^\\s*([0-9]+(?:[+-][0-9]+)?)/g, (_, base, exponent) => base + toSuperscript(exponent))
    .replace(/\\b(cm|mm|km|m)2\\b/gi, "$1²")
    .replace(/\\b(cm|mm|km|m)3\\b/gi, "$1³")
    .replace(/\b1\/2\b/g, "½")
    .replace(/\b1\/3\b/g, "⅓")
    .replace(/\b2\/3\b/g, "⅔")
    .replace(/\b1\/4\b/g, "¼")
    .replace(/\b3\/4\b/g, "¾")
    .replace(/√\\s+/g, "√")
    .replace(/\\s{2,}/g, " ");
}
function cleanQuestionStem(question: Question, stem: string) {
  // Alguns PDFs de origem trazem as 5 alternativas dentro do próprio enunciado.
  // O simulador já renderiza as alternativas nos botões; remover esse bloco evita
  // duplicação e também impede que o rodapé do PDF apareça como parte da questão.
  if (question.alternativas && /(?:^|\n)\s*A\)\s*/m.test(stem)) {
    return stem.replace(/(?:^|\n)\s*A\)\s*[\s\S]*$/m, "").trim();
  }
  return stem.trim();
}

function getQuestionAlternativeText(question: Question, letter: "A" | "B" | "C" | "D") {
  const visualText = getVisualMathText(question);
  if (visualText?.alternativas?.[letter]) return formatMathSymbols(visualText.alternativas[letter]!);
  const fallback = question.alternativas?.[letter];
  if (fallback && !/^Alternativa visual (original )?[A-E]$/i.test(fallback)) return formatMathSymbols(fallback);
  if (question.numeroOriginal && getVisualMathReference(question)) {
    return `Alternativa ${letter} — consulte a opção correspondente no material visual acima.`;
  }
  return formatMathSymbols(fallback ?? `Alternativa ${letter}`);
}

function QuestionCard({ q, displayNumber, selected, onSelect, revealed, onReveal, canReveal, disabled }: { q: Question; displayNumber: number; selected?: string; onSelect: (answer: string) => void; revealed: boolean; onReveal: () => void; canReveal: boolean; disabled: boolean }) {
const meta = getAreaMeta(q.area);
  return (
    <article className="question-card" style={{ "--question-color": meta.color, "--question-pale": meta.pale } as React.CSSProperties}>
      <div className="question-meta">
        <span className="question-number">{String(displayNumber).padStart(2, "0")}</span>
        <span className="topic-chip" style={{ backgroundColor: meta.pale, color: meta.color }}>{q.habilidade}</span>
        <span className="question-reference">{q.referencia}</span>
      </div>
      <p className="question-stem">{formatMathSymbols(cleanQuestionStem(q, getVisualMathText(q)?.enunciado ?? q.enunciado))}</p>
      {(() => {
        const visual = getVisualMathReference(q);
        if (!visual) return null;
        const visualLabels = q.visualAlternativas;
        return (
          <details className="question-visual-reference" open>
            <summary>Material visual original · questão {q.numeroOriginal ?? displayNumber}</summary>
            <div className="question-visual-frame" style={{ minHeight: 260, maxHeight: 700, overflow: "hidden", border: "1px solid #D8D0C3", background: "#fff", display: "flex", justifyContent: "center" }}>
              <img
                src={visual.imageUrl}
                alt={`Material visual original da questão ${q.numeroOriginal ?? displayNumber}`}
                loading="lazy"
                decoding="async"
                style={{ display: "block", width: "100%", height: "auto", maxWidth: 980, objectFit: "contain" }}
              />
            </div>
            <p className="question-visual-caption" style={{ margin: 0, padding: "9px 12px", fontSize: 11, lineHeight: 1.45, color: "#657083" }}>
              Material visual armazenado localmente no projeto para evitar dependência de visualizadores e URLs externos. As quatro opções do simulador preservam as alternativas originais indicadas pelas letras acima{visualLabels ? ` (${Object.entries(visualLabels).map(([sim, original]) => `${sim}→${original}`).join(", ")} )` : ""}.
              {q.visualAlternativaRemovida ? ` A alternativa original ${q.visualAlternativaRemovida} foi retirada para manter o formato de 4 opções do simulador.` : ""}
            </p>
          </details>
        );
      })()}
      <div className="alternatives" role="radiogroup" aria-label={`Alternativas da questão ${displayNumber}`}>
        {(["A", "B", "C", "D"] as const).map((letter) => {
          const isSelected = selected === letter;
          const isCorrect = revealed && q.correta === letter;
          const isWrong = revealed && isSelected && q.correta !== letter;
          return (
            <button key={letter} disabled={disabled} className={`alternative ${isSelected ? "selected" : ""} ${isCorrect ? "correct" : ""} ${isWrong ? "wrong" : ""}`} onClick={() => onSelect(letter)} role="radio" aria-checked={isSelected}>
              <span>{letter}</span>
              <span>{getQuestionAlternativeText(q, letter)}</span>
              {isCorrect && <Check size={16} strokeWidth={2.8} />}
            </button>
          );
        })}
      </div>
      <div className="question-footer">
        <span>Referência curricular: <strong>{q.areaCurta}</strong></span>
        {canReveal ? <button className="reveal-button" onClick={onReveal}>{revealed ? `Resposta correta: ${q.correta} · Ocultar explicação` : "Ver resposta comentada"}</button> : <span className="answer-locked">Finalize a correção para consultar o comentário.</span>}
      </div>
      {revealed && <p className="answer-explanation"><strong>Por quê?</strong> {q.justificativa}</p>}
    </article>
  );
}

export default function Home() {
  const { user, profileRole, loadProfileRole, loading, isAuthenticated, login, signup, signInWithGoogle, recover, requiresPasswordReset, completePasswordRecovery, logout, refresh } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authInitialMode, setAuthInitialMode] = useState<"login" | "signup">("login");
  const [authContext, setAuthContext] = useState<"student" | "developer">("student");
  const profile = useMemo(() => initialProfile(), []);
  const [activeArea, setActiveArea] = useState<FilterArea>("Todas");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const [showKey, setShowKey] = useState(false);
  const [studentName, setStudentName] = useState(profile.studentName);
  const [classroom, setClassroom] = useState(profile.classroom);
  const [classroomCode, setClassroomCode] = useState("");
  const [myClassrooms, setMyClassrooms] = useState<Classroom[]>([]);
  const [classroomNotice, setClassroomNotice] = useState("");
  const [studentAccessType, setStudentAccessType] = useState<StudentAccessType>("classroom");
  const [studentProfileId, setStudentProfileId] = useState<StudentProfileId | null>(null);
  const [studentIdentityConfirmed, setStudentIdentityConfirmed] = useState(false);
  const [studentIdentityNotice, setStudentIdentityNotice] = useState("");
  const [localProfileId] = useState(profile.localId);
  const [submitted, setSubmitted] = useState(false);
  const [submittedScore, setSubmittedScore] = useState<ReturnType<typeof calculateSimulationScore> | null>(null);
  const [timerPreset, setTimerPreset] = useState<TimerPreset>("dia1");
  const [remainingSeconds, setRemainingSeconds] = useState(timerPresets.dia1.seconds);
  const [timerRunning, setTimerRunning] = useState(false);
  const [attempts, setAttempts] = useState<Attempt[]>(initialAttempts);
  const [cycleDate, setCycleDate] = useState(() => new Date());
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedReviewAttemptId, setSelectedReviewAttemptId] = useState<string | null>(null);
  const [progressNotice, setProgressNotice] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("todas");
  const [teacherSort, setTeacherSort] = useState("score");
  const [teacherStudents, setTeacherStudents] = useState<TeacherStudent[]>([]);
  const [teacherStudentsLoading, setTeacherStudentsLoading] = useState(false);
  const [teacherAttemptsLoading, setTeacherAttemptsLoading] = useState(false);
  const [teacherRefreshKey, setTeacherRefreshKey] = useState(0);
  const [teacherStudentsNotice, setTeacherStudentsNotice] = useState("");
  const [selectedTeacherStudentId, setSelectedTeacherStudentId] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<"local" | "syncing" | "cloud" | "error">("local");
  const [remotePayload, setRemotePayload] = useState<string | null>(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [cadernoOutputMode, setCadernoOutputMode] = useState<CadernoOutputMode>("print");
  const [selectedCadernoBlocks, setSelectedCadernoBlocks] = useState<string[]>(() => CADERNO_PRINT_BLOCKS.map((block) => block.id));
 const teacherMode = hasInstitutionalTeacherAccess(user ? { ...user, role: profileRole ?? undefined } : null);
const accessUnlocked = teacherMode || isAuthenticated;
const studentKey = user?.id || localProfileId;
  const classroomKey = normalizeIdentity(classroom, "turma-local");

  useEffect(() => {
    if (!user) return;
    const metadata = user.user_metadata as Record<string, unknown> | undefined;
    const accountName = typeof metadata?.full_name === "string"
      ? metadata.full_name
      : typeof metadata?.name === "string"
        ? metadata.name
        : "";    if (!studentName.trim() && accountName.trim()) setStudentName(accountName.trim());
  }, [user, studentName]);
  const studentAttempts = attempts.filter((attempt) => attempt.studentKey === studentKey);
  const { attemptsUsed, attemptsRemaining, maxAttemptsReached, currentAttemptNumber } = getDailyAttemptCycle(attempts, studentKey, cycleDate);
  const todayStudentAttempts = useMemo(
    () => studentAttempts
      .filter((attempt) => isCurrentLocalDay(attempt.createdAt, cycleDate))
      .sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt)),
    [studentAttempts, cycleDate],
  );
  const evolutionChartData = useMemo(
    () => todayStudentAttempts.map((attempt, index) => ({
      name: `T${index + 1}`,
      acertos: attempt.correct,
      percentual: attempt.percentage,
    })),
    [todayStudentAttempts],
  );
  const selectedReviewAttempt = useMemo(() => todayStudentAttempts.find((attempt) => attempt.id === selectedReviewAttemptId) ?? todayStudentAttempts[todayStudentAttempts.length - 1] ?? null, [todayStudentAttempts, selectedReviewAttemptId]);
  const selectedReviewWrongQuestions = useMemo(() => {
    if (!selectedReviewAttempt?.answers) return [];
    const questionById = new Map(questions.map((question) => [question.id, question]));
    return Object.entries(selectedReviewAttempt.answers).map(([displayNumber, selected]) => {
      if (!selected) return null;
      const number = Number(displayNumber);
      const questionId = selectedReviewAttempt.questionIds?.[number];
      const question = questionId ? questionById.get(questionId) : null;
      if (!question || selected === question.correta) return null;
      return { displayNumber: number, selected, question };
    }).filter((item): item is { displayNumber: number; selected: string; question: Question } => Boolean(item)).sort((a, b) => a.displayNumber - b.displayNumber);
  }, [selectedReviewAttempt]);
  const usedQuestionIds = useMemo(
    () => todayStudentAttempts.flatMap((attempt) => Object.values(attempt.questionIds ?? {})),
    [todayStudentAttempts],
  );
  const attemptQuestions = useMemo(
    () => accessUnlocked
      ? buildAttemptQuestions(questions, studentKey, currentAttemptNumber, usedQuestionIds)
      : [],
    [accessUnlocked, studentKey, currentAttemptNumber, usedQuestionIds],
  );
  const selectedClassroom = useMemo(
    () => studentAccessType === "classroom"
      ? myClassrooms.find((entry) => normalizeIdentity(entry.name, "turma-local") === classroomKey)
      : undefined,
    [myClassrooms, classroomKey, studentAccessType],
  );
  const studentProfile = STUDENT_PROFILE_OPTIONS.find((option) => option.id === studentProfileId) ?? null;
  const studentReadyToAnswer = teacherMode || (
    studentIdentityConfirmed
    && Boolean(studentName.trim())
    && (studentAccessType === "independent" || Boolean(selectedClassroom))
  );

  const openAuth = (mode: "login" | "signup", context: "student" | "developer" = "student") => {
    setAuthInitialMode(mode);
    setAuthContext(context);
    setAuthOpen(true);
  };
  const loginFromDialog = async (email: string, password: string) => {
    await login(email, password);
    if (authContext !== "developer") return;
    const current = await refresh();
    const role = await loadProfileRole(current);
    if (hasInstitutionalTeacherAccess(current ? { ...current, role: role ?? undefined } : null)) return;
    await logout().catch(() => undefined);
    throw new Error("developer-role-required");
  };

  const filteredQuestions = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("pt-BR");
    return attemptQuestions.filter((q) => {
      const matchArea = activeArea === "Todas" || q.area === activeArea;
      const searchable = `${q.enunciado} ${q.habilidade} ${q.referencia}`.toLocaleLowerCase("pt-BR");
      return matchArea && (!needle || searchable.includes(needle));
    });
  }, [activeArea, attemptQuestions, query]);

  // O aluno responde uma questão por vez; após marcar a alternativa, a próxima é exibida automaticamente.
  const pageSize = 1;
  const totalPages = Math.max(1, Math.ceil(filteredQuestions.length / pageSize));
  const currentQuestions = filteredQuestions.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => setPage(1), [activeArea, query]);

  useEffect(() => {
    if (!timerRunning || remainingSeconds <= 0) return;
    const interval = window.setInterval(() => setRemainingSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(interval);
  }, [timerRunning, remainingSeconds]);

  useEffect(() => {
    if (remainingSeconds === 0) setTimerRunning(false);
  }, [remainingSeconds]);

  useEffect(() => {
    const now = new Date();
    const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timeout = window.setTimeout(() => setCycleDate(new Date()), nextDay.getTime() - now.getTime() + 100);
    return () => window.clearTimeout(timeout);
  }, [cycleDate]);

  useEffect(() => {
    window.localStorage.setItem(ATTEMPTS_STORAGE_KEY, JSON.stringify(attempts));
  }, [attempts]);

    useEffect(() => {
    try {
      const draft = JSON.parse(window.localStorage.getItem(PROGRESS_STORAGE_KEY) || "null");
      if (draft?.answers && typeof draft.savedAt === "string" && isCurrentLocalDay(draft.savedAt)) { setAnswers(draft.answers); setRemainingSeconds(draft.remainingSeconds ?? timerPresets.dia1.seconds); setProgressNotice("Progresso anterior restaurado."); }
    } catch { /* ignorar rascunho inválido */ }
  }, []);

  useEffect(() => {
    let active = true;
    if (loading || !isAuthenticated) return () => { active = false; };

    const authContextAfterRedirect = typeof window !== "undefined"
      ? window.sessionStorage.getItem("simulado-auth-context")
      : null;
    if (authContextAfterRedirect !== "developer") return () => { active = false; };
    window.sessionStorage.removeItem("simulado-auth-context");

    void loadProfileRole(user)
      .then((role) => {
        if (!active) return;
        if (!hasInstitutionalTeacherAccess(user ? { ...user, role: role ?? undefined } : null)) {
          setProgressNotice("Esta conta não possui acesso ao professor desenvolvedor.");
          void logout().catch(() => undefined);
        }
      })
      .catch(() => {
        if (active) {
          setProgressNotice("Não foi possível validar o acesso do professor.");
          void logout().catch(() => undefined);
        }
      });

    return () => { active = false; };
  }, [loading, isAuthenticated, user, loadProfileRole, logout]);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated || teacherMode) {
      setStudentAccessType("classroom");
      return () => { active = false; };
    }
    void loadStudentAccessType()
      .then((type) => {
        if (!active) return;
        setStudentAccessType(type);
        if (type === "independent") setStudentProfileId("independent");
      })
      .catch(() => {
        if (active) setStudentAccessType("classroom");
      });
    return () => { active = false; };
  }, [isAuthenticated, teacherMode, user?.id]);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setMyClassrooms([]);
      setClassroomNotice("");
      return () => { active = false; };
    }
    void loadMyClassrooms()
      .then((items) => {
        if (!active) return;
        setMyClassrooms(items);
        if (studentAccessType === "independent") {
          setStudentProfileId("independent");
          return;
        }
        const current = items.find((item) => normalizeIdentity(item.name, "turma-local") === classroomKey)
          ?? items.find((item) => item.code === "3A2026")
          ?? items.find((item) => item.code === "3B2026")
          ?? items.find((item) => item.code === "ELITE26");
        if (current) {
          setClassroom(current.name);
          setClassroomCode(current.code);
          const profile = STUDENT_PROFILE_OPTIONS.find((option) => option.code === current.code);
          if (profile) setStudentProfileId(profile.id);
        }
      })
      .catch(() => {
        if (active) setClassroomNotice("Não foi possível carregar as turmas agora.");
      });
    return () => { active = false; };
  }, [isAuthenticated, classroomKey, studentAccessType]);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setRemotePayload(null);
      setSyncState("local");
      return () => { active = false; };
    }
    setSyncState("syncing");
    void loadRemoteProgress()
      .then((result) => {
        if (!active) return;
        setRemotePayload(result.payload);
        setSyncState("cloud");
      })
      .catch(() => {
        if (active) setSyncState("error");
      });
    return () => { active = false; };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setTeacherAttemptsLoading(false);
      return () => { active = false; };
    }
    if (teacherMode) setTeacherAttemptsLoading(true);

    void loadRemoteAttempts(teacherMode ? "all" : "own")
      .then((remoteAttempts) => {
        if (!active) return;
        if (teacherMode) {
          setTeacherAttemptsLoading(false);
          setTeacherStudentsNotice("");
        }

        const byId = new Map<string, Attempt>();
        for (const attempt of attempts) byId.set(attempt.id, attempt);

        const questionById = new Map(questions.map((question) => [question.id, question]));
        const remoteRecords: Attempt[] = remoteAttempts.map((remote) => {
          const answers: Record<number, string> = {};
          const areaBuckets = new Map<string, { correct: number; answered: number }>();

          for (const answer of remote.answers) {
            if (answer.selected_answer) answers[answer.display_number] = answer.selected_answer;
            const question = questionById.get(answer.question_id);
            if (!question) continue;
            const bucket = areaBuckets.get(question.area) ?? { correct: 0, answered: 0 };
            if (answer.selected_answer) bucket.answered += 1;
            if (answer.is_correct) bucket.correct += 1;
            areaBuckets.set(question.area, bucket);
          }

          const byArea: AttemptArea[] = Object.keys(areaMeta).map((area) => {
            const bucket = areaBuckets.get(area) ?? { correct: 0, answered: 0 };
            return {
              short: getAreaMeta(area).short,
              correct: bucket.correct,
              answered: bucket.answered,
              blank: Math.max(0, 25 - bucket.answered),
              percentage: Math.round((bucket.correct / 25) * 100),
            };
          });

          return {
            id: remote.id,
            studentKey: remote.student_id,
            studentName: remote.student_name || "Estudante",
            classroomKey: normalizeIdentity(remote.classroom_name || "", "turma-local"),
            classroomId: remote.classroom_id,
            classroom: remote.classroom_name || "Turma local",
            createdAt: remote.finished_at,
            correct: remote.correct,
            answered: remote.answered,
            percentage: remote.percentage,
            remainingSeconds: remote.remaining_seconds,
            byArea,
            answers,
            questionIds: Object.fromEntries(remote.answers.map((answer) => [answer.display_number, answer.question_id])),
          };
        });

        for (const record of remoteRecords) byId.set(record.id, record);

        const merged = Array.from(byId.values()).sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
        setAttempts(teacherMode ? remoteRecords : merged);
      })
      .catch((error) => {
        if (active) {
          if (teacherMode) {
            setTeacherAttemptsLoading(false);
            setTeacherStudentsNotice(error instanceof Error ? error.message : "Não foi possível carregar os resultados centralizados.");
          }
          setProgressNotice("Não foi possível atualizar o painel centralizado agora; os registros locais continuam disponíveis.");
        }
      });

    return () => { active = false; };
  }, [isAuthenticated, teacherMode, user?.id, teacherRefreshKey]);

  useEffect(() => {
    if (!remotePayload || submitted) return;
    try {
      const synced = JSON.parse(remotePayload);
      if (synced.answers) setAnswers(synced.answers);
      if (typeof synced.remainingSeconds === "number") setRemainingSeconds(synced.remainingSeconds);
      if (typeof synced.studentName === "string") setStudentName(synced.studentName);
      if (typeof synced.classroom === "string") setClassroom(synced.classroom);
      setSyncState("cloud");
      setProgressNotice("Progresso sincronizado com sua conta.");
    } catch { /* ignorar dados remotos inválidos */ }
  }, [remotePayload]);

  const simulationScore = useMemo(() => calculateSimulationScore(attemptQuestions, answers), [attemptQuestions, answers]);
  const visibleScore = submitted ? submittedScore : null;
  const visibleAreaPerformance = visibleScore?.byArea ?? [];
  const attemptPrintQuestions = useMemo(() => attemptQuestions.map((question, index) => ({ ...question, numero: index + 1 })), [attemptQuestions]);
  const selectedCadernoQuestions = useMemo(() => selectCadernoPrintBlocks(attemptPrintQuestions, CADERNO_PRINT_BLOCKS, selectedCadernoBlocks), [attemptPrintQuestions, selectedCadernoBlocks]);
  const selectedCadernoPages = useMemo(() => paginateCadernoForPrint(selectedCadernoQuestions), [selectedCadernoQuestions]);
  const selectedCadernoLabel = useMemo(() => CADERNO_PRINT_BLOCKS.filter((block) => selectedCadernoBlocks.includes(block.id)).map((block) => block.label.replace("Bloco ", "B.")).join(" · "), [selectedCadernoBlocks]);
  const areaPerformance = simulationScore.byArea;
  const totalAnswered = simulationScore.answered;
  const totalCorrect = simulationScore.correct;
  const overallPercentage = simulationScore.percentage;
  const isCriticalTime = remainingSeconds > 0 && remainingSeconds <= 10 * 60;
  const isTimeOver = remainingSeconds === 0;
  const classAttempts = attempts.filter((attempt) => attempt.classroomKey === classroomKey);
  const uniqueStudentsInClass = new Set(classAttempts.map((attempt) => attempt.studentKey)).size;
  const classAverage = classAttempts.length ? Math.round(classAttempts.reduce((sum, attempt) => sum + attempt.percentage, 0) / classAttempts.length) : 0;
  const anonymousRanking = useMemo(() => {
    const recordsByStudent = new Map<string, Attempt[]>();
    classAttempts.forEach((attempt) => recordsByStudent.set(attempt.studentKey, [...(recordsByStudent.get(attempt.studentKey) || []), attempt]));
    return Array.from(recordsByStudent.values())
      .map((records: Attempt[]) => ({ best: Math.max(...records.map((record: Attempt) => record.percentage)), attempts: records.length }))
      .sort((a, b) => b.best - a.best)
      .map((record, index) => ({ ...record, label: `Participante ${String.fromCharCode(65 + index)}` }));
  }, [classAttempts]);
  const teacherRows = useMemo(() => attempts.filter((attempt) => teacherFilter === "todas" || (teacherFilter === "__independent__" ? !attempt.classroomId : (attempt.classroomId || attempt.classroomKey) === teacherFilter)).sort((a, b) => teacherSort === "score" ? b.percentage - a.percentage : +new Date(b.createdAt) - +new Date(a.createdAt)), [attempts, teacherFilter, teacherSort]);
  const teacherClassrooms = useMemo(() => Array.from(new Map(attempts.filter((attempt) => Boolean(attempt.classroomId)).map((attempt) => [attempt.classroomId || attempt.classroomKey, attempt.classroom])).entries()), [attempts]);
  const selectedTeacherClassroomId = teacherFilter === "todas" || teacherFilter === "__independent__"
    ? null
    : (myClassrooms.find((item) => item.id === teacherFilter)?.id ?? null);
  const selectedTeacherIndependent = teacherFilter === "__independent__";
  const latestAttempt = studentAttempts[studentAttempts.length - 1];

  const selectedTeacherStudent = useMemo(
    () => teacherStudents.find((student) => student.student_id === selectedTeacherStudentId) ?? null,
    [teacherStudents, selectedTeacherStudentId],
  );
  const selectedTeacherAttempts = useMemo(
    () => selectedTeacherStudentId
      ? teacherRows.filter((attempt) => attempt.studentKey === selectedTeacherStudentId).sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt))
      : [],
    [teacherRows, selectedTeacherStudentId],
  );
  const selectedTeacherLatest = selectedTeacherAttempts[selectedTeacherAttempts.length - 1] ?? null;
  const teacherClassMetrics = useMemo(() => {
    const areaMap = new Map<string, { correct: number; answered: number; attempts: number }>();
    teacherRows.forEach((attempt) => attempt.byArea.forEach((area) => {
      const current = areaMap.get(area.short) ?? { correct: 0, answered: 0, attempts: 0 };
      current.correct += area.correct;
      current.answered += area.answered;
      current.attempts += 1;
      areaMap.set(area.short, current);
    }));
    return Object.keys(areaMeta).map((area) => {
      const short = getAreaMeta(area).short;
      const value = areaMap.get(short) ?? { correct: 0, answered: 0, attempts: 0 };
      return { short, percentage: value.attempts ? Math.round((value.correct / (value.attempts * 25)) * 100) : 0, correct: value.correct, answered: value.answered, attempts: value.attempts };
    });
  }, [teacherRows]);
  const selectedTeacherWrongAnswers = useMemo(() => {
    if (!selectedTeacherLatest) return [];
    const questionById = new Map(questions.map((question) => [question.id, question]));
    return Object.entries(selectedTeacherLatest.answers).map(([displayNumber, selected]) => {
      const number = Number(displayNumber);
      const questionId = selectedTeacherLatest.questionIds?.[number];
      const question = questionId ? questionById.get(questionId) : questions.find((item) => item.numero === number);
      if (!question || selected === question.correta) return null;
      return { displayNumber: number, selected, question };
    }).filter((item): item is { displayNumber: number; selected: string; question: Question } => Boolean(item)).sort((a, b) => a.displayNumber - b.displayNumber);
  }, [selectedTeacherLatest]);
  const wrongQuestions = useMemo(() => {
    if (!latestAttempt?.answers) return [];
    const questionById = new Map(questions.map((question) => [question.id, question]));
    return Object.entries(latestAttempt.answers)
      .map(([displayNumber, selected]) => {
        if (!selected) return null;
        const number = Number(displayNumber);
        const questionId = latestAttempt.questionIds?.[number];
        const question = questionId ? questionById.get(questionId) : null;
        if (!question || selected === question.correta) return null;
        return { displayNumber: number, selected, question };
      })
      .filter((item): item is { displayNumber: number; selected: string; question: Question } => Boolean(item))
      .sort((a, b) => a.displayNumber - b.displayNumber);
  }, [latestAttempt]);
  const performanceMessage = totalAnswered === 0
    ? "Registre suas respostas para iniciar a leitura do desempenho."
    : overallPercentage >= 70 ? "Bom domínio do conjunto. Observe as áreas com menor percentual para orientar a revisão."
      : overallPercentage >= 45 ? "é uma base de aprendizagem consistente; use os detalhes por área para organizar a próxima revisão."
        : "O resultado mostra pontos concretos para retomar. Priorize uma área por vez e compare as respostas comentadas.";

  useEffect(() => {
    let active = true;
    if (!teacherMode) {
      setTeacherStudents([]);
      setTeacherStudentsNotice("");
      return () => { active = false; };
    }

    const teacherClassroomIds = myClassrooms
      .filter((item) => item.is_teacher)
      .map((item) => item.id);
    const classroomIds = selectedTeacherClassroomId ? [selectedTeacherClassroomId] : teacherClassroomIds;
    const shouldIncludeIndependent = teacherFilter === "todas" || selectedTeacherIndependent;

    setTeacherStudentsLoading(true);
    const loadClassroomStudents = classroomIds.length
      ? Promise.all(classroomIds.map((classroomId) => loadTeacherStudents(classroomId)))
      : Promise.resolve([] as TeacherStudent[][]);

    void loadClassroomStudents
      .then((groups) => {
        if (!active) return;
        const merged = new Map<string, TeacherStudent>();
        for (const group of groups) {
          for (const student of group) {
            const previous = merged.get(student.student_id);
            if (!previous) {
              merged.set(student.student_id, { ...student });
              continue;
            }
            const attemptsCount = previous.attempts_count + student.attempts_count;
            const weightedAverage = attemptsCount
              ? Math.round(
                  (previous.average_percentage * previous.attempts_count
                    + student.average_percentage * student.attempts_count) / attemptsCount,
                )
              : 0;
            merged.set(student.student_id, {
              ...previous,
              attempts_count: attemptsCount,
              average_percentage: weightedAverage,
              best_percentage: Math.max(previous.best_percentage, student.best_percentage),
              last_attempt_at: [previous.last_attempt_at, student.last_attempt_at]
                .filter(Boolean)
                .sort()
                .at(-1) ?? null,
            });
          }
        }

        if (shouldIncludeIndependent) {
          const independentAttempts = attempts.filter((attempt) => !attempt.classroomId);
          const independentByStudent = new Map<string, Attempt[]>();
          for (const attempt of independentAttempts) {
            const list = independentByStudent.get(attempt.studentKey) ?? [];
            list.push(attempt);
            independentByStudent.set(attempt.studentKey, list);
          }
          for (const [studentId, records] of independentByStudent) {
            const ordered = [...records].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
            const latest = ordered.at(-1) ?? null;
            merged.set(studentId, {
              student_id: studentId,
              student_name: latest?.studentName || "Aluno avulso",
              student_email: null,
              joined_at: latest?.createdAt || new Date().toISOString(),
              attempts_count: records.length,
              average_percentage: Math.round(records.reduce((sum, attempt) => sum + attempt.percentage, 0) / records.length),
              best_percentage: Math.max(...records.map((attempt) => attempt.percentage)),
              last_attempt_at: latest?.createdAt || null,
            });
          }
        }

        setTeacherStudents(Array.from(merged.values()));
        setTeacherStudentsNotice("");
      })
      .catch((error) => {
        if (!active) return;
        setTeacherStudents([]);
        setTeacherStudentsNotice(error instanceof Error ? error.message : "Não foi possível carregar os alunos.");
      })
      .finally(() => {
        if (active) setTeacherStudentsLoading(false);
      });
    return () => { active = false; };
  }, [teacherMode, myClassrooms, selectedTeacherClassroomId, selectedTeacherIndependent, teacherFilter, attempts]);

  const handleCreateClassroom = async () => {
    const name = window.prompt("Nome da turma");
    if (!name?.trim()) return;
    const generatedCode = window.crypto?.randomUUID
      ? window.crypto.randomUUID().replace(/-/g, "").slice(0, 6).toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
    try {
      const created = await createClassroom(name.trim(), generatedCode);
      setMyClassrooms((current) => [...current, created]);
      setClassroom(created.name);
      setClassroomCode(created.code);
      setClassroomNotice(`Turma criada. Código: ${created.code}`);
    } catch (error) {
      setClassroomNotice(error instanceof Error ? error.message : "Não foi possível criar a turma.");
    }
  };

  const handleJoinClassroom = async () => {
    if (studentAccessType !== "classroom") {
      setClassroomNotice("O perfil Aluno avulso não possui vínculo com turmas. Mude para Aluno de turma para entrar em uma.");
      return;
    }
    if (!classroomCode.trim()) return;
    try {
      const joined = await joinClassroom(classroomCode.trim());
      setMyClassrooms((current) => current.some((item) => item.id === joined.id) ? current : [...current, joined]);
      setClassroom(joined.name);
      setClassroomCode(joined.code);
      setClassroomNotice(`Você entrou na turma ${joined.name}.`);
    } catch (error) {
      setClassroomNotice(error instanceof Error ? error.message : "Não foi possível entrar na turma.");
    }
  };

  const selectStudentProfile = async (profileId: StudentProfileId) => {
    if (!isAuthenticated || teacherMode) return;
    const option = STUDENT_PROFILE_OPTIONS.find((item) => item.id === profileId);
    if (!option) return;

    try {
      setStudentIdentityNotice("");
      setStudentIdentityConfirmed(false);

      if (option.id === "independent") {
        await saveStudentAccessType("independent");
        setStudentAccessType("independent");
        setStudentProfileId("independent");
        setClassroom("");
        setClassroomCode("");
        setStudentIdentityNotice("Perfil Aluno Avulso selecionado. Você fará o simulado sem vínculo com uma turma.");
        return;
      }

      await saveStudentAccessType("classroom");
      setStudentAccessType("classroom");

      const existing = myClassrooms.find((item) => item.code === option.code);
      const joined = existing ?? await joinClassroom(option.code!);
      setMyClassrooms((current) => current.some((item) => item.id === joined.id) ? current : [...current, joined]);
      setStudentProfileId(option.id);
      setClassroom(joined.name);
      setClassroomCode(joined.code);
      setStudentIdentityNotice(`Perfil ${joined.name} selecionado. Agora confirme seu nome para iniciar.`);
    } catch (error) {
      setStudentIdentityNotice(error instanceof Error ? error.message : "Não foi possível selecionar este perfil.");
    }
  };

  const confirmStudentIdentity = async () => {
    if (!isAuthenticated || teacherMode) return;
    const trimmedName = studentName.trim();
    if (!trimmedName) {
      setStudentIdentityNotice("Informe seu nome para ser identificado na turma.");
      return;
    }
    if (studentAccessType === "classroom" && !selectedClassroom) {
      setStudentIdentityNotice("Selecione uma turma antes de iniciar.");
      return;
    }
    try {
      await saveStudentDisplayName(trimmedName);
      window.localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify({
        ...profile,
        studentName: trimmedName,
        classroom,
      }));
      setStudentName(trimmedName);
      setStudentIdentityConfirmed(true);
      setStudentIdentityNotice(`Identificação confirmada: ${trimmedName}. Você já pode iniciar a primeira questão.`);
    } catch (error) {
      setStudentIdentityNotice(error instanceof Error ? error.message : "Não foi possível salvar sua identificação.");
    }
  };

  const changeStudentAccessType = async (nextType: StudentAccessType) => {
    const target = nextType === "independent" ? "independent" : (studentProfileId ?? "3a");
    await selectStudentProfile(target);
  };

  const setAnswer = (numero: number, answer: string) => {
    if (submitted || maxAttemptsReached || !studentReadyToAnswer) return;
    const isFirstAnswer = Object.keys(answers).length === 0;
    setAnswers((current) => ({ ...current, [numero]: answer }));
    setSubmitted(false);

    // O cronômetro só começa quando o aluno responde a primeira questão.
    if (isFirstAnswer && remainingSeconds > 0) {
      setTimerRunning(true);
    }

    // Como cada questão é exibida isoladamente, avançamos automaticamente.
    if (page < totalPages) {
      window.setTimeout(() => setPage((currentPage) => Math.min(totalPages, currentPage + 1)), 0);
    }
  };
  const reveal = (numero: number) => setRevealed((current) => {
    const next = new Set(current);    if (next.has(numero)) next.delete(numero);
    else next.add(numero);
    return next;
  });
  const resetTimer = () => {
    if (submitted || maxAttemptsReached) return;
    setRemainingSeconds(timerPresets[timerPreset].seconds);
    setTimerRunning(false);
  };
  const chooseTimerPreset = (preset: TimerPreset) => {
    if (submitted || maxAttemptsReached) return;
    setTimerPreset(preset);
    setTimerRunning(false);
    setRemainingSeconds(timerPresets[preset].seconds);
  };
  const syncProgress = async (payload: { answers: Record<number, string>; remainingSeconds: number; studentName: string; classroom: string; attempts: Attempt[]; savedAt: string }) => {
    if (!isAuthenticated) return;
    setSyncState("syncing");
    try {
      await saveRemoteProgress(JSON.stringify(payload));
      setSyncState("cloud");
      setProgressNotice("Progresso salvo e sincronizado com sua conta.");
    } catch {
      setSyncState("error");
      setProgressNotice("Progresso salvo neste navegador; a sincronização será tentada no próximo salvamento.");
    }
  };
  const finishSimulation = async () => {
    if (submitted || maxAttemptsReached || totalAnswered === 0) return;

    // Ao finalizar, o cronômetro precisa parar imediatamente, inclusive se
    // o registro no Supabase demorar ou retornar erro para uma nova tentativa.
    setTimerRunning(false);

    const now = new Date().toISOString();
    const record: Attempt = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      studentKey,
      studentName: studentName.trim() || "Estudante local",
      classroomKey: studentAccessType === "independent" ? "aluno-avulso" : classroomKey,
      classroomId: selectedClassroom?.id ?? null,
      classroom: studentAccessType === "independent" ? "Aluno avulso" : (classroom.trim() || "Turma local"),
      createdAt: now,
      correct: totalCorrect,
      answered: totalAnswered,
      percentage: overallPercentage,
      remainingSeconds,
      byArea: areaPerformance.map((area) => ({ short: area.short, correct: area.correct, answered: area.answered, blank: area.blank, percentage: area.percentage })),
      answers: { ...answers },
      questionIds: Object.fromEntries(attemptQuestions.map((question) => [question.numero, question.id])),
    };

    if (isAuthenticated) {
      setProgressNotice("Registrando resultado...");
      try {
        await saveRemoteAttempt({
          studentName,
          studentEmail: user?.email || "",
          classroomName: studentAccessType === "independent" ? null : (classroom.trim() || "Turma local"),
          classroomId: selectedClassroom?.id ?? null,
          remainingSeconds,
          answers,
          questions: attemptQuestions.map((question) => ({
            id: question.id,
            numero: question.numero,
            correta: question.correta as "A" | "B" | "C" | "D",
          })),
        });
        const updated = [...attempts, record];
        setAttempts(updated);
        // Uma tentativa concluída não deve voltar como rascunho na próxima abertura.
        window.localStorage.removeItem(PROGRESS_STORAGE_KEY);
        void syncProgress({
          answers: {},
          remainingSeconds: timerPresets[timerPreset].seconds,
          studentName,
          classroom,
          attempts: updated,
          savedAt: new Date().toISOString(),
        });
        setRemotePayload(null);
        setProgressNotice("Resultado registrado e respostas armazenadas no Supabase.");
      } catch (error) {
        setProgressNotice(error instanceof Error ? error.message : "Não foi possível registrar o resultado.");
        return;
      }
    } else {
      setAttempts((current) => [...current, record]);
      window.localStorage.removeItem(PROGRESS_STORAGE_KEY);
    }

    // Após a correção, todas as questões ficam disponíveis para revisão do aluno.
    // Isso mantém o feedback associado à própria questão e evita depender do modo professor.
    setRevealed(new Set(attemptQuestions.map((question) => question.numero)));
    setSubmittedScore(simulationScore);
    setSubmitted(true);
    setTimerRunning(false);
    scrollToSection("resultado");
  };
  const beginNextAttempt = () => {
    if (maxAttemptsReached) return;
    setAnswers({});
    setRevealed(new Set());
    window.localStorage.removeItem(PROGRESS_STORAGE_KEY);
    setSubmitted(false);
    setSubmittedScore(null);
    setRemainingSeconds(timerPresets[timerPreset].seconds);
    setTimerRunning(false);
    setActiveArea("Todas");
    setQuery("");
    setPage(1);
    scrollToSection("questoes");
  };
 const saveProgress = () => {
  const payload = { answers, remainingSeconds, studentName, classroom, attempts, savedAt: new Date().toISOString() };
window.localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(payload));
   if (isAuthenticated) void syncProgress(payload);
    else setProgressNotice("Progresso salvo neste navegador. Entre na sua conta para sincronizá-lo entre dispositivos.");
  };
  const exportTeacherStudentPdf = () => {
    if (!selectedTeacherStudent || !selectedTeacherAttempts.length) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 18;
    const ensure = (height = 14) => {
      if (y > pageHeight - height) {
        doc.addPage();
        y = 18;
      }
    };
    doc.setFillColor(29, 42, 68);
    doc.rect(0, 0, pageWidth, 30, "F");
    doc.setTextColor(255, 250, 242);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("SIMULADO ENEM - RELATÓRIO DO ALUNO", 15, 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Desempenho, evolução e respostas incorretas", 15, 21);
    y = 42;
    doc.setTextColor(29, 42, 68);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(selectedTeacherStudent.student_name || "Estudante", 15, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(91, 102, 117);
    doc.text((selectedTeacherStudent.student_email || "Sem e-mail") + " · " + (selectedTeacherLatest?.classroom || "Turmas selecionadas"), 15, y + 6);
    y += 17;
    if (selectedTeacherLatest) {
      doc.setFillColor(244, 237, 227);
      doc.roundedRect(15, y, pageWidth - 30, 25, 2, 2, "F");
      doc.setTextColor(29, 42, 68);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.text(selectedTeacherLatest.percentage + "%", 21, y + 16);
      doc.setFontSize(9);
      doc.text(selectedTeacherLatest.correct + "/100 acertos · " + selectedTeacherLatest.answered + "/100 respondidas", 52, y + 11);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(91, 102, 117);
      doc.text("Última tentativa: " + new Date(selectedTeacherLatest.createdAt).toLocaleString("pt-BR"), 52, y + 18);
      y += 34;
    }
    doc.setTextColor(29, 42, 68);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Evolução T1 → T2 → T3", 15, y);
    y += 7;
    selectedTeacherAttempts.forEach((attempt, index) => {
      ensure(10);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("T" + (index + 1), 15, y);
      doc.setFont("helvetica", "normal");
      doc.text(attempt.correct + "/100 · " + attempt.percentage + "% · " + new Date(attempt.createdAt).toLocaleDateString("pt-BR"), 28, y);
      y += 6;
    });
    y += 4;
    if (selectedTeacherLatest) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Desempenho por área", 15, y);
      y += 7;
      selectedTeacherLatest.byArea.forEach((area) => {
        ensure(10);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(area.short, 15, y);
        doc.setFont("helvetica", "normal");
        doc.text(area.correct + "/25 · " + area.percentage + "% · " + area.answered + "/25 respondidas", 62, y);
        y += 6;
      });
      y += 4;
    }
    ensure(16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(29, 42, 68);
    doc.text("Questões incorretas da última tentativa (" + selectedTeacherWrongAnswers.length + ")", 15, y);
    y += 8;
    if (!selectedTeacherWrongAnswers.length) {
      ensure(8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("Nenhuma questão incorreta registrada.", 15, y);
      y += 6;
    }
    selectedTeacherWrongAnswers.forEach((item) => {
      const header = "Questão " + String(item.displayNumber).padStart(2, "0") + " · sua resposta: " + item.selected + " · correta: " + item.question.correta;
      const rawText = String(item.question.enunciado || "").split(String.fromCharCode(13)).join("").split(String.fromCharCode(10)).map((line) => line.replace(/[ \\t]+/g, " ").trim()).join(" ").replace(/\\s+([A-E]\\s*[).:-])/g, "\\n$1").trim();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      const lines = doc.splitTextToSize(rawText, pageWidth - 30);
      const lineHeight = 4;
      const firstPageCapacity = pageHeight - 18 - 18;
      const estimatedHeight = 6 + Math.min(lines.length, Math.floor(firstPageCapacity / lineHeight)) * lineHeight + 3;
      if (y + estimatedHeight > pageHeight - 15) {
        doc.addPage();
        y = 18;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(29, 42, 68);
      doc.text(header, 15, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(75, 88, 105);
      let offset = 0;
      while (offset < lines.length) {
        const availableLines = Math.max(1, Math.floor((pageHeight - 16 - y) / lineHeight));
        const chunk = lines.slice(offset, offset + availableLines);
        doc.text(chunk, 15, y, { maxWidth: pageWidth - 30 });
        y += chunk.length * lineHeight;
        offset += chunk.length;
        if (offset < lines.length) {
          doc.addPage();
          y = 18;
        }
      }
      y += 4;
      doc.setTextColor(29, 42, 68);
    });
    doc.save("relatorio-" + normalizeIdentity(selectedTeacherStudent.student_name || "estudante", "estudante").replace(/[^a-z0-9]+/g, "-") + ".pdf");
  };

  const exportTeacherClassPdf = () => {
    if (!teacherRows.length) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const selectedClassName = teacherFilter === "todas" ? "Todas as turmas" : teacherFilter === "__independent__" ? "Alunos avulsos" : (myClassrooms.find((item) => item.id === teacherFilter)?.name || "Turma");
    let y = 42;
    const drawPageHeader = (continuation = false) => {
      doc.setFillColor(29, 42, 68);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.setTextColor(255, 250, 242);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("SIMULADO ENEM - RELATÓRIO DA TURMA", 15, 14);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text(selectedClassName + " · " + teacherRows.length + " tentativa(s)" + (continuation ? " · continuação" : ""), 15, 21);
      y = 42;
    };
    const ensure = (height = 10) => {
      if (y + height > pageHeight - 15) {
        doc.addPage();
        drawPageHeader(true);
      }
    };
    drawPageHeader();
    doc.setTextColor(29, 42, 68);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Resumo", 15, y);
    y += 7;
    const average = Math.round(teacherRows.reduce((sum, row) => sum + row.percentage, 0) / teacherRows.length);
    const participants = new Set(teacherRows.map((row) => row.studentKey)).size;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Alunos com tentativa: " + participants, 15, y);
    doc.text("Média das tentativas: " + average + "%", 75, y);
    doc.text("Maior resultado: " + Math.max(...teacherRows.map((row) => row.percentage)) + "%", 145, y);
    y += 12;
    ensure(15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Desempenho por área", 15, y);
    y += 7;
    teacherClassMetrics.forEach((area) => {
      ensure(9);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text(area.short, 15, y);
      doc.setFont("helvetica", "normal");
      doc.text(area.percentage + "% · " + area.correct + " acertos em " + (area.attempts * 25) + " itens · " + area.answered + " respondidos", 65, y);
      y += 6;
    });
    y += 4;
    ensure(15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Tentativas por aluno", 15, y);
    y += 7;
    const rows = Array.from(new Map(teacherRows.map((row) => [row.studentKey, row])).values()).map((latest) => {
      const records = teacherRows.filter((item) => item.studentKey === latest.studentKey).sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
      return { latest: records[records.length - 1], count: records.length, best: Math.max(...records.map((item) => item.percentage)) };
    }).sort((a, b) => b.latest.percentage - a.latest.percentage);
    rows.forEach(({ latest, count, best }) => {
      ensure(12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(29, 42, 68);
      doc.text(latest.studentName || "Estudante", 15, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(75, 88, 105);
      doc.text(count + " tentativa(s) · última " + latest.percentage + "% · melhor " + best + "% · " + new Date(latest.createdAt).toLocaleDateString("pt-BR"), 75, y);
      y += 6;
    });
    doc.save("relatorio-" + normalizeIdentity(selectedClassName, "turma").replace(/[^a-z0-9]+/g, "-") + ".pdf");
  };

  const exportAllAttemptsCsv = () => {
    const header = ["Estudante", "Turma", "Data", "Acertos", "Respondidas", "Percentual", "Tempo restante", "Linguagens", "Humanas", "Natureza", "Matemática"];
    const rows = attempts.map((attempt) => {
      const byArea = new Map(attempt.byArea.map((area) => [area.short, area.percentage]));
      return [attempt.studentName, attempt.classroom, new Date(attempt.createdAt).toLocaleString("pt-BR"), attempt.correct, attempt.answered, `${attempt.percentage}%`, formatDuration(attempt.remainingSeconds), byArea.get("Linguagens") ?? "", byArea.get("Humanas") ?? "", byArea.get("Natureza") ?? "", byArea.get("Matemática") ?? ""];
    });
    const content = "\uFEFF" + [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
    const href = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a"); link.href = href; link.download = "resultados-simulado-todas-as-turmas.csv"; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(href);
  };
  const exportPdfReport = () => {
    const reportName = studentName.trim() || "Estudante";
    const todayAttempts = studentAttempts
      .filter((attempt) => isCurrentLocalDay(attempt.createdAt, new Date()))
      .sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
    const reportAttempts = todayAttempts.length ? todayAttempts : [studentAttempts[studentAttempts.length - 1]].filter(Boolean);
    const latestReportAttempt = reportAttempts[reportAttempts.length - 1];
    const firstReportAttempt = reportAttempts[0];

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 20;

    const addPageIfNeeded = (height = 18) => {
      if (y > pageHeight - height) {
        doc.addPage();
        y = 20;
      }
    };

    const drawAreaRow = (area: AttemptArea, index: number, previous?: AttemptArea) => {
      addPageIfNeeded(25);
      const meta = getAreaMeta(area.short);
      const hex = meta.color.replace("#", "");
      doc.setFillColor(parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16));
      doc.rect(15, y - 4, 3, 17, "F");
      doc.setTextColor(29, 42, 68);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.text(area.short, 23, y + 1);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(91, 102, 117);
      const evolution = previous ? area.correct - previous.correct : 0;
      const evolutionText = previous ? ` · Evolução: ${evolution > 0 ? "+" : ""}${evolution} acerto(s)` : "";
      doc.text(`${area.correct}/25 acertos · ${area.answered}/25 respondidas · ${area.blank} em branco${evolutionText}`, 23, y + 7);
      doc.setTextColor(29, 42, 68);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(`${area.percentage}%`, pageWidth - 29, y + 4, { align: "right" });
      y += 22;
    };

    doc.setFillColor(29, 42, 68);
    doc.rect(0, 0, pageWidth, 34, "F");
    doc.setTextColor(255, 250, 242);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text("SIMULADO ENEM - RELATÓRIO DE DESEMPENHO", 15, 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Acertos, desempenho por área e evolução nas tentativas diárias", 15, 23);

    doc.setTextColor(29, 42, 68);
    y = 46;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(reportName, 15, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(91, 102, 117);
    doc.text(`Relatório de ${reportAttempts.length} tentativa(s) registrada(s) hoje · Gerado em ${new Date().toLocaleDateString("pt-BR")}`, 15, y + 6);

    // Resumo da tentativa mais recente
    const latestScore = latestReportAttempt || {
      correct: totalCorrect,
      percentage: overallPercentage,
      answered: totalAnswered,
      byArea: areaPerformance.map((area) => ({ short: area.short, correct: area.correct, answered: area.answered, blank: area.blank, percentage: area.percentage })),
    };
    doc.setFillColor(244, 237, 227);
    doc.roundedRect(15, y + 15, pageWidth - 30, 30, 2, 2, "F");
    doc.setTextColor(29, 42, 68);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text(`${latestScore.percentage}%`, 22, y + 34);
    doc.setFontSize(9);
    doc.text("ÚLTIMA TENTATIVA", 52, y + 27);
    doc.setFontSize(12);
    doc.text(`${latestScore.correct} acertos em 100 itens`, 52, y + 34);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(91, 102, 117);
    doc.text(`${latestScore.answered} respondidas · ${100 - latestScore.answered} em branco`, 52, y + 40);
    y += 56;

    // Evolução das três tentativas
    doc.setTextColor(29, 42, 68);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Evolução das tentativas do dia", 15, y);
    y += 8;

    reportAttempts.forEach((attempt, index) => {
      addPageIfNeeded(22);
      const previous = index > 0 ? reportAttempts[index - 1] : undefined;
      const delta = previous ? attempt.correct - previous.correct : 0;
      const deltaText = previous ? ` ${delta > 0 ? "↑ +" : delta < 0 ? "↓ " : "→ "}${delta} acerto(s)` : "Primeira tentativa";
      doc.setFillColor(238, 241, 244);
      doc.roundedRect(15, y - 4, pageWidth - 30, 16, 1.5, 1.5, "F");
      doc.setTextColor(29, 42, 68);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text(`Tentativa ${index + 1}`, 20, y + 2);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(91, 102, 117);
      doc.text(new Date(attempt.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }), 20, y + 8);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(29, 42, 68);
      doc.text(`${attempt.correct}/100 · ${attempt.percentage}%`, 78, y + 4);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(delta > 0 ? 73 : delta < 0 ? 160 : 91, delta > 0 ? 116 : delta < 0 ? 85 : 102, delta > 0 ? 100 : delta < 0 ? 70 : 117);
      doc.text(deltaText, pageWidth - 20, y + 4, { align: "right" });
      y += 20;
    });

    // Tabela de evolução por área
    addPageIfNeeded(48);
    y += 2;
    doc.setTextColor(29, 42, 68);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Acertos por área e evolução", 15, y);
    y += 7;

    const areaHeaders = ["Área", ...reportAttempts.map((_, index) => `T${index + 1}`), "Evolução"];
    const colX = [15, 91, 111, 131, 151, 174];
    doc.setFontSize(7.5);
    doc.setTextColor(91, 102, 117);
    areaHeaders.forEach((header, index) => doc.text(header, colX[index], y));
    y += 5;

    const areaNames = ["Linguagens", "Humanas", "Natureza", "Matemática"];
    areaNames.forEach((areaName) => {
      addPageIfNeeded(12);
      const values = reportAttempts.map((attempt) => attempt.byArea.find((area) => area.short === areaName)?.correct ?? 0);
      const first = values[0] ?? 0;
      const last = values[values.length - 1] ?? 0;
      const delta = last - first;
      doc.setDrawColor(220, 225, 230);
      doc.line(15, y + 2, pageWidth - 15, y + 2);
      doc.setTextColor(29, 42, 68);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.2);
      doc.text(areaName, 15, y);
      doc.setFont("helvetica", "normal");
      values.forEach((value, index) => doc.text(`${value}/25`, colX[index + 1], y));
      doc.setFont("helvetica", "bold");
      doc.text(`${delta > 0 ? "+" : ""}${delta}`, colX[5], y);
      y += 7;
    });

    addPageIfNeeded(38);
    y += 8;
    doc.setDrawColor(200, 77, 58);
    doc.setLineWidth(.7);
    doc.line(15, y, pageWidth - 15, y);
    y += 9;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(29, 42, 68);
    doc.text("Leitura da evolução", 15, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.6);
    doc.setTextColor(91, 102, 117);

    const overallDelta = latestReportAttempt && firstReportAttempt ? latestReportAttempt.correct - firstReportAttempt.correct : 0;
    const evolutionText = reportAttempts.length > 1
      ? `Entre a primeira e a última tentativa de hoje, o desempenho global variou ${overallDelta > 0 ? "+" : ""}${overallDelta} acerto(s) (${firstReportAttempt.correct}/100 → ${latestReportAttempt.correct}/100). O relatório registra o desempenho por área para permitir acompanhar onde houve mudança ao longo das tentativas.`
      : "A evolução será preenchida quando houver mais de uma tentativa concluída no mesmo dia.";
    const observation = doc.splitTextToSize(`${evolutionText} A pontuação representa acertos simples neste simulado; não equivale à nota TRI do ENEM.`, pageWidth - 30);
    doc.text(observation, 15, y);

    doc.setFontSize(7.5);
    doc.setTextColor(120, 128, 138);
    doc.text("Simulado ENEM Interativo · Material autoral reformulado para prática pedagógica.", 15, pageHeight - 12);
    const safeName = reportName.normalize("NFD").replace(/[\\u0300-\\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "estudante";
    doc.save(`relatorio-desempenho-simulado-enem-${safeName}.pdf`);
  };
  const exportCadernoPdf = (cadernoQuestions: readonly Question[], selectionLabel: string) => {
    if (!cadernoQuestions.length) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let page = 1;
    let y = 35;
    const drawHeader = () => {
      doc.setFillColor(29, 42, 68);
      doc.rect(0, 0, pageWidth, 25, "F");
      doc.setTextColor(255, 250, 242);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("SIMULADO ENEM - CADERNO DE QUESTÕES", 15, 12);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text("Material autoral reformulado para aplicação pedagógica", 15, 18);
      doc.text(`${selectionLabel} · Folha ${String(page).padStart(2, "0")}`, pageWidth - 15, 18, { align: "right" });
      doc.setTextColor(29, 42, 68);
    };
    const drawFooter = (pageNumber: number) => {      doc.setDrawColor(207, 199, 184);
      doc.line(15, pageHeight - 12, pageWidth - 15, pageHeight - 12);
      doc.setTextColor(112, 126, 142);
      doc.setFontSize(7);
      doc.text("Simulado ENEM Interativo", 15, pageHeight - 7);
      doc.text(`${pageNumber}`, pageWidth - 15, pageHeight - 7, { align: "right" });
    };
    const nextPage = () => { doc.addPage(); page += 1; y = 35; drawHeader(); };

    drawHeader();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(["Escola: ______________________________________________", "Professor(a): ________________________________________", "Estudante: __________________________________________", "Turma: ____________________    Data: ____ / ____ / ______"], 15, y, { lineHeightFactor: 1.55 });
    y += 27;
    cadernoQuestions.forEach((question) => {
      const questionLines = doc.splitTextToSize(question.enunciado, pageWidth - 30) as string[];
      const alternativeLines = (["A", "B", "C", "D"] as const).flatMap((letter) => doc.splitTextToSize(`${letter}. ${question.alternativas[letter]}`, pageWidth - 38) as string[]);
      const requiredHeight = 12 + questionLines.length * 4.4 + alternativeLines.length * 3.8;
      if (y + requiredHeight > pageHeight - 20) nextPage();
      doc.setTextColor(200, 77, 58);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.text(`Questão ${String(question.numero).padStart(2, "0")}`, 15, y);
      doc.setTextColor(101, 112, 128);
      doc.setFontSize(7.5);
      doc.text(question.areaCurta, pageWidth - 15, y, { align: "right" });
      y += 5;
      doc.setTextColor(29, 42, 68);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.text(questionLines, 15, y, { lineHeightFactor: 1.35 });
      y += questionLines.length * 4.4 + 2.2;
      doc.setFontSize(8.6);
      (["A", "B", "C", "D"] as const).forEach((letter) => {
        const lines = doc.splitTextToSize(`${letter}. ${question.alternativas[letter]}`, pageWidth - 38) as string[];
        doc.text(lines, 20, y, { lineHeightFactor: 1.25 });
        y += lines.length * 3.8 + 1;
      });
      doc.setDrawColor(222, 214, 203);
      doc.line(15, y + 1, pageWidth - 15, y + 1);
      y += 6;
    });
    const pages = doc.getNumberOfPages();
    for (let pageNumber = 1; pageNumber <= pages; pageNumber += 1) { doc.setPage(pageNumber); drawFooter(pageNumber); }
    // Download explícito por Blob: evita depender do mecanismo interno de
    // download do jsPDF e funciona de forma consistente em navegadores atuais.
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const downloadLink = document.createElement("a");
    downloadLink.href = pdfUrl;
    downloadLink.download = cadernoPdfFilename();
    downloadLink.rel = "noopener";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
  };
  const toggleCadernoBlock = (blockId: string) => setSelectedCadernoBlocks((current) => current.includes(blockId) ? current.filter((id) => id !== blockId) : [...current, blockId]);
  const requestCadernoOutput = (outputMode: CadernoOutputMode) => {
    setCadernoOutputMode(outputMode);
    setPrintDialogOpen(true);
  };
  const confirmCadernoOutput = () => {
    if (!selectedCadernoQuestions.length) return;
    if (cadernoOutputMode === "pdf") exportCadernoPdf(selectedCadernoQuestions, selectedCadernoLabel);
    else printPreview(selectedCadernoQuestions, selectedCadernoLabel);
    setPrintDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F3EC] text-[#1D2A44]">
      <header className="topbar">
        <a href="#inicio" className="brand" aria-label="Ir para o início">
          <img src="/simulado-enem-logo.svg" alt="Símbolo do Simulado ENEM" className="brand-mark" />
          <span className="brand-word"><strong>SIMULADO</strong><em>ENEM</em></span>
        </a>
        <nav className={`main-nav ${menuOpen ? "open" : ""}`} aria-label="Navegação principal">
          <button onClick={() => { scrollToSection("matriz"); setMenuOpen(false); }}>Matriz</button>
          <button onClick={() => { scrollToSection("questoes"); setMenuOpen(false); }}>Questões</button>
          <button onClick={() => { scrollToSection("resultado"); setMenuOpen(false); }}>Resultado</button>
          {maxAttemptsReached && <button onClick={() => { scrollToSection("acompanhamento"); setMenuOpen(false); }}>Acompanhamento</button>}
          {teacherMode && <button onClick={() => { scrollToSection("correcao"); setMenuOpen(false); }}>Correção</button>}
          <button onClick={() => { scrollToSection("fontes"); setMenuOpen(false); }}>Fontes</button>
        </nav>
        <div className="top-actions">
          {!loading && (isAuthenticated ? <><span className={`top-timer ${syncState}`}>{syncState === "cloud" ? "✓ Sincronizado" : syncState === "syncing" ? "↻ Sincronizando" : syncState === "error" ? "! Salvo localmente" : "• Sem salvar"}</span><button className="top-timer" onClick={() => void logout()}>Sair da conta</button></> : <button className="top-timer" onClick={() => openAuth("login")}>Acesso aluno</button>)}
          <button className={`top-timer ${isCriticalTime ? "critical" : ""}`} onClick={() => scrollToSection("questoes")} aria-label="Ir para o cronômetro"><Timer size={15} /><span>{formatDuration(remainingSeconds)}</span></button>
          {teacherMode && <Button className="print-button" onClick={() => requestCadernoOutput("print")}><Printer size={16} /> Imprimir caderno</Button>}
          <button className="menu-button" aria-label="Abrir menu" onClick={() => setMenuOpen((value) => !value)}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </header>

<StudentAuthDialog
  open={authOpen}
  onOpenChange={setAuthOpen}
  initialMode={authInitialMode}
  context={authContext}
  onLogin={loginFromDialog}
  onSignup={signup}
  onRecover={recover}
  onGoogleLogin={signInWithGoogle}
  requiresPasswordReset={requiresPasswordReset}
  onCompletePasswordRecovery={completePasswordRecovery}
/>     
      <AlertDialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto border-[#D8D0C3] bg-[#FDFBF6] text-[#1D2A44] sm:max-w-3xl">
          <AlertDialogHeader><AlertDialogTitle className="font-serif text-2xl">Preparar caderno docente</AlertDialogTitle><AlertDialogDescription className="text-[#5B697A]">Confira as quatro áreas e revise a paginação A4 antes de {cadernoOutputMode === "pdf" ? "gerar o PDF" : "abrir a impressão"}.</AlertDialogDescription></AlertDialogHeader>
          <section className="mt-1 border-y border-[#DED6C8] py-4" aria-labelledby="blocos-caderno"><div className="mb-3 flex items-end justify-between gap-4"><div><h3 id="blocos-caderno" className="text-sm font-extrabold text-[#1D2A44]">Blocos de aplicação</h3><p className="mt-1 text-xs text-[#657083]">Cada bloco reúne 25 questões de uma área. Para o caderno completo, mantenha os quatro blocos selecionados.</p></div><span className="shrink-0 text-xs font-bold text-[#C84D3A]">{selectedCadernoQuestions.length} itens</span></div><div className="grid gap-2 sm:grid-cols-2">{CADERNO_PRINT_BLOCKS.map((block) => { const selected = selectedCadernoBlocks.includes(block.id); return <label key={block.id} className={`cursor-pointer border p-3 transition-colors ${selected ? "border-[#1D2A44] bg-[#EEF2F4]" : "border-[#D8D0C3] bg-white"}`}><input type="checkbox" className="sr-only" checked={selected} onChange={() => toggleCadernoBlock(block.id)} /><span className="flex items-start gap-2"><span className={`mt-0.5 grid h-4 w-4 place-items-center border text-[10px] ${selected ? "border-[#1D2A44] bg-[#1D2A44] text-white" : "border-[#AEB7BD] bg-white text-transparent"}`}><Check size={11} /></span><span><strong className="block text-xs text-[#1D2A44]">{block.label}</strong><span className="mt-1 block text-[11px] leading-snug text-[#657083]">{block.description}</span></span></span></label>; })}</div></section>
          <section className="mt-4" aria-labelledby="previa-caderno"><div className="flex items-end justify-between gap-4"><div><h3 id="previa-caderno" className="text-sm font-extrabold text-[#1D2A44]">Pré-visualização da impressão</h3><p className="mt-1 text-xs text-[#657083]">A paginação usa até quatro questões por folha, com cabeçalho, rodapé e identificação.</p></div><span className="shrink-0 text-xs font-bold text-[#497464]">{selectedCadernoPages.length} {selectedCadernoPages.length === 1 ? "folha" : "folhas"}</span></div>{selectedCadernoPages.length ? <div className="mt-3 grid max-h-48 grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3">{selectedCadernoPages.map((previewPage, index) => <article className="min-h-28 border border-[#D8D0C3] bg-white p-3 shadow-[3px_3px_0_#E7E0D5]" key={`${previewPage[0]?.numero}-${index}`}><div className="flex justify-between border-b border-[#E4DCD0] pb-1 text-[9px] font-extrabold uppercase tracking-wider text-[#697483]"><span>Folha {String(index + 1).padStart(2, "0")}</span><span>{previewPage.length} itens</span></div><ol className="mt-2 grid gap-1 text-[10px] leading-snug text-[#3F5066]">{previewPage.map((question) => <li key={question.numero}><strong className="text-[#C84D3A]">{String(question.numero).padStart(2, "0")}</strong> · {question.areaCurta}</li>)}</ol></article>)}</div> : <p className="mt-3 border border-dashed border-[#C84D3A] bg-[#FCE8E3] p-3 text-xs text-[#8B3529]">Selecione pelo menos um bloco para continuar.</p>}</section>
          <AlertDialogFooter className="mt-5"><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction disabled={!selectedCadernoQuestions.length} className="bg-[#1D2A44] text-white hover:bg-[#2B4164] disabled:cursor-not-allowed disabled:opacity-50" onClick={confirmCadernoOutput}>{cadernoOutputMode === "pdf" ? <><FileDown size={15} /> Gerar PDF</> : <><Printer size={15} /> Abrir impressão</>}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <main id="inicio">
        <section className="hero-section">
          <div className="hero-content">
            <div className="eyebrow"><span></span> Caderno autoral reformulado · 100 itens</div>
            <h1>Revisão que vira <i>diagnóstico.</i></h1>
            <p>Um caderno de simulado para aplicar, interpretar e corrigir: **25 questões por área**, quatro alternativas e uma estrutura pronta para a sala de aula.</p>
            <div className="hero-actions">
              <Button onClick={() => { if (accessUnlocked) scrollToSection("questoes"); else openAuth("login", "student"); }} className="hero-primary">Iniciar simulado <ArrowRight size={17} /></Button>
              {teacherMode && <button className="hero-secondary" onClick={() => downloadFile("mascara", attemptPrintQuestions)}><ArrowDownToLine size={17} /> Baixar máscara</button>}
            </div>
            <div className="hero-note"><Info size={15} /> Itens autorais inspirados em habilidades e temas de provas oficiais; não são reproduções literais.</div>
          </div>
          <div className="hero-image-wrap" aria-hidden="true">
            <img src="/simulado-enem-hero.svg" alt="" className="hero-image" />
            <div className="hero-stamp"><strong>4</strong><span>alternativas<br />por item</span></div>
          </div>
        </section>

        <section className="rail-layout" id="matriz">
          <aside className="section-rail">
            <span className="rail-index">01</span>
            <span className="rail-line"></span>
            <p>Mapa<br />do caderno</p>
          </aside>
          <div className="section-body matrix-layout">
            <div className="section-intro">
              <div><span className="eyebrow"><span></span> Matriz de composição</span><h2>Quatro áreas.<br /><i>Uma leitura equilibrada.</i></h2></div>
              <p>O ENEM reúne quatro áreas com 45 itens cada. Neste recorte de 100 questões, a divisão proporcional resulta em 25 itens por área.[<a href="#fontes">1</a>]</p>
            </div>
            <div className="stat-strip">
              <div><strong>100</strong><span>questões</span></div>
              <div><strong>25</strong><span>por área</span></div>
              <div><strong>4</strong><span>alternativas</span></div>
              <div><strong>2</strong><span>blocos de aplicação</span></div>
            </div>
            <div className="chart-card bar-card">
              <div className="card-heading"><div><span className="mini-label">DISTRIBUIÇÃO</span><h3>Itens por área de conhecimento</h3></div><BarChart3 size={19} /></div>
              <div className="chart-visual">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={areaSummary} layout="vertical" margin={{ top: 6, right: 24, left: 2, bottom: 2 }}>
                    <XAxis type="number" domain={[0, 28]} hide />
                    <YAxis dataKey="short" type="category" width={92} tick={{ fill: "#435064", fontSize: 12, fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "#EAE3D6" }} contentStyle={{ borderRadius: 0, border: "1px solid #D9D0C1", boxShadow: "none", fontFamily: "Manrope", fontSize: 12 }} formatter={(value) => [`${value} questões`, "Quantidade"]} />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>{areaSummary.map((entry, index) => <Cell key={entry.short} fill={chartColors[index]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="chart-caption">Passe o cursor pelas barras para consultar a distribuição proporcional.</p>
            </div>
            <div className="chart-card pie-card">
              <div className="card-heading"><div><span className="mini-label">OPERAÇÕES PRIORIZADAS</span><h3>Como o caderno mobiliza competências</h3></div></div>
              <div className="pie-with-legend">
                <div className="pie-visual"><ResponsiveContainer width="100%" height="100%"><PieChart><Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D9D0C1", boxShadow: "none", fontFamily: "Manrope", fontSize: 12 }} formatter={(value) => [`${value} itens`, ""]} /><Pie data={operationData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={78} paddingAngle={3} stroke="none">{operationData.map((entry, index) => <Cell key={entry.name} fill={chartColors[index]} />)}</Pie></PieChart></ResponsiveContainer></div>
                <div className="legend-list">{operationData.map((entry, index) => <div key={entry.name}><span style={{ background: chartColors[index] }}></span><p>{entry.name}<strong>{entry.value}%</strong></p></div>)}</div>
              </div>
              <p className="chart-caption">Classificação editorial dos itens do próprio simulado, para apoiar a revisão por operação cognitiva.</p>
            </div>
          </div>
        </section>

        <section className="area-section">
          <div className="area-heading"><span className="eyebrow"><span></span> Percurso de estudo</span><h2>Selecione uma área e encontre<br /><i>o tipo de desafio.</i></h2></div>
          <div className="area-cards">
            {areaSummary.map((entry) => {
              const longName = entry.area as AreaName;
              const meta = areaMeta[longName];
              return <button key={entry.area} className="area-card" style={{ "--area-color": meta.color, "--area-pale": meta.pale } as React.CSSProperties} onClick={() => { setActiveArea(longName); scrollToSection("questoes"); }}>
                <div><span className="area-index">{meta.index}</span><span className="area-count">{entry.count} itens</span></div>
                <h3>{entry.short}</h3><p>{longName.replace(" e suas Tecnologias", "")}</p><span className="area-arrow"><ArrowRight size={17} /></span>
              </button>;
            })}
          </div>
        </section>

        <section className="method-section">
          <div className="method-image"><img src="/enem-skills-collage.svg" alt="Colagem abstrata que representa quatro campos do conhecimento" /></div>
          <div className="method-copy">
            <span className="eyebrow"><span></span> Como foi elaborado</span>
            <h2>Uma compilação para <i>ensinar</i>, não para copiar.</h2>
            <p>O banco combina itens autorais, elaborados a partir da Matriz de Referência, com um acervo de questões de Matemática do ENEM de 2009 a 2018. As questões do acervo foram adaptadas para quatro alternativas. Os itens visuais com alternativas gráficas ainda incompletas permanecem fora da seleção automática até receberem tratamento integral.</p>
            <div className="method-points"><p><Check size={16} /> 2.463 itens ativos no banco de prática</p><p><Check size={16} /> 1.500 itens autorais + 963 itens de Matemática 2009–2018</p><p><Check size={16} /> Caderno, gabarito e máscara em separado</p></div>
            <button className="text-button" onClick={() => scrollToSection("fontes")}>Ver referências oficiais <ArrowRight size={16} /></button>
          </div>
        </section>

        <section className="rail-layout question-section" id="questoes">
          <aside className="section-rail"><span className="rail-index">02</span><span className="rail-line"></span><p>Banco<br />de questões</p></aside>
          <div className="section-body">
            <div className="question-header">
              <div><span className="eyebrow"><span></span> Leitura ativa</span><h2>Banco de questões<br /><i>para explorar.</i></h2></div>
              <span className="workbook-note">folha de aplicação<br />marque uma opção</span>
              <div className="question-actions"><Button variant="outline" className="download-outline" onClick={() => accessUnlocked && exportCadernoPdf(attemptPrintQuestions, "Caderno completo · 100 questões")}><ArrowDownToLine size={16} /> Baixar caderno · 100 questões</Button>{teacherMode && <Button className="print-button" onClick={() => requestCadernoOutput("print")}><Printer size={16} /> Imprimir</Button>}</div>
            </div>
            <section className="student-dashboard" id="resultado" aria-label="Painel de desempenho do estudante">
              <span className="workbook-folio">FOLHA 01 · APLICAÇÃO E ACOMPANHAMENTO</span>
              {accessUnlocked && <>
              <div className="student-dashboard-top">
                <div><span className="eyebrow"><span></span> Modo de realização</span><h3>Seu percurso, em tempo real.</h3><p>{isAuthenticated ? "Suas respostas e tentativas podem ser sincronizadas com esta conta. A pontuação é por acerto simples e não corresponde à  nota TRI." : "As respostas e tentativas ficam neste navegador até você entrar em uma conta. A pontuação é por acerto simples e não corresponde à nota TRI."}</p></div>
                <div className="student-profile">
                  {!teacherMode ? <>
                    <div className="student-profile-selector">
                      <span className="mini-label">PERFIL DO ALUNO</span>
                      <p className="text-xs text-[#657083]">Escolha onde você fará o simulado. O vínculo da turma será feito automaticamente.</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {STUDENT_PROFILE_OPTIONS.map((option) => <button key={option.id} type="button" disabled={submitted || maxAttemptsReached} onClick={() => void selectStudentProfile(option.id)} className={`border px-3 py-2 text-left transition ${studentProfileId === option.id ? "border-[#1D4C72] bg-[#EAF1F6] text-[#1D2A44]" : "border-[#D8D0C3] bg-white text-[#435064] hover:border-[#9BA8B7]"}`}>
                          <strong className="block text-sm">{option.label}</strong><span className="block text-[10px]">{option.description}</span>
                        </button>)}
                      </div>
                    </div>
                    <label className="student-name"><UserRound size={16} /><span>Nome para identificação na turma</span><input value={studentName} disabled={submitted || maxAttemptsReached} onChange={(event) => { setStudentName(event.target.value); setStudentIdentityConfirmed(false); }} placeholder="Digite seu nome completo" /></label>
                    {studentProfile && <p className="text-xs font-bold text-[#657083]">Você será identificado como <strong>{studentName.trim() || "seu nome"}</strong> em <strong>{studentProfile.label}</strong>.</p>}
                    {studentIdentityNotice && <p className="text-xs font-bold text-[#497464]">{studentIdentityNotice}</p>}
                    <button type="button" onClick={() => void confirmStudentIdentity()} disabled={submitted || maxAttemptsReached || !studentProfileId || !studentName.trim()} className="timer-start w-full justify-center disabled:cursor-not-allowed disabled:opacity-50">
                      <Check size={15} /> Confirmar identificação e começar
                    </button>
                    {isAuthenticated && <p className="text-[10px] font-bold text-[#657083]">Sua conta autenticada também protege seus resultados e histórico.</p>}
                  </> : <>
                    <label className="student-name"><UserRound size={16} /><span>Nome no relatório</span><input value={studentName} disabled={submitted || maxAttemptsReached} onChange={(event) => setStudentName(event.target.value)} placeholder="Como quer ser identificado?" /></label>
                    <label className="student-name classroom-name"><GraduationCap size={16} /><span>Turma local</span><input value={classroom} disabled={submitted || maxAttemptsReached} onChange={(event) => setClassroom(event.target.value)} placeholder="Ex.: 3.º ano A" /></label>
                    {isAuthenticated && <p className="text-xs font-bold text-[#657083]">Conta conectada: {user?.email || "conta autenticada"}</p>}
                  </>}
                </div>
              </div>
              <div className={`attempt-limit ${maxAttemptsReached ? "limit-reached" : ""}`}><div><span className="attempt-kicker">LIMITE DIÁRIO DE REALIZAÇÃO</span><strong>{attemptsUsed} de {MAX_DAILY_ATTEMPTS} tentativas concluídas hoje</strong><p>{maxAttemptsReached ? "As três tentativas de hoje foram concluídas. Amanhã, você terá três novas tentativas disponíveis; seu histórico permanece salvo." : `Você ainda pode concluir ${attemptsRemaining} ${attemptsRemaining === 1 ? "tentativa" : "tentativas"} hoje.`}</p></div><div className="attempt-dots" aria-label={`${attemptsUsed} de ${MAX_DAILY_ATTEMPTS} tentativas utilizadas hoje`}>{Array.from({ length: MAX_DAILY_ATTEMPTS }, (_, index) => <span className={index < attemptsUsed ? "used" : ""} key={index}>{index + 1}</span>)}</div></div>
              <div className={`exam-timer ${isCriticalTime ? "critical" : ""} ${isTimeOver ? "finished" : ""}`}>
                <div className="timer-copy"><div><Timer size={19} /><span>CRONôMETRO DE SIMULAÇÃO</span></div><p>{isTimeOver ? "Tempo encerrado" : isCriticalTime ? "Atenção: últimos 10 minutos" : "Escolha o dia e inicie quando estiver pronto."}</p></div>
                <div className="timer-display" aria-live="polite">{formatDuration(remainingSeconds)}</div>
                <div className="timer-controls"><select value={timerPreset} disabled={submitted || maxAttemptsReached} onChange={(event) => chooseTimerPreset(event.target.value as TimerPreset)} aria-label="Selecionar duração da prova"><option value="dia1">1.º dia · 5h30</option><option value="dia2">2.º dia · 5h</option></select><button onClick={() => setTimerRunning((value) => !value)} disabled={isTimeOver || submitted || maxAttemptsReached} className="timer-start">{timerRunning ? <Pause size={15} /> : <Play size={15} />}{timerRunning ? "Pausar" : "Iniciar"}</button><button onClick={resetTimer} disabled={submitted || maxAttemptsReached} className="timer-reset" aria-label="Reiniciar cronômetro"><RotateCcw size={15} /></button></div>
              </div>
              <div className="live-score">
                <div className="score-orbit" style={{ "--score": `${visibleScore ? visibleScore.percentage * 3.6 : 0}deg` } as React.CSSProperties}><div><strong>{visibleScore ? `${visibleScore.percentage}%` : "—"}</strong><span>{submitted ? "acertos" : "aguardando correção"}</span></div></div>
                <div className="score-copy"><span className="mini-label">DESEMPENHO GLOBAL · TENTATIVA {currentAttemptNumber}</span><h3>{visibleScore ? `${visibleScore.correct} de 100 itens corretos` : "Resultado oculto até finalizar"}</h3><p>{totalAnswered} respostas registradas · {100 - totalAnswered} itens em branco</p><p className="score-message">{submitted ? (maxAttemptsReached ? "Ciclo de três tentativas concluído. Consulte o acompanhamento local abaixo." : performanceMessage) : "A correção e os percentuais por área serão exibidos somente após finalizar a tentativa."}</p></div>
                <div className="score-actions"><Button className="score-finalize" onClick={finishSimulation} disabled={submitted || maxAttemptsReached || totalAnswered === 0}><Trophy size={16} /> {maxAttemptsReached ? "Ciclo concluído" : submitted ? "Resultado registrado" : "Finalizar e corrigir"}</Button><Button variant="outline" className="pdf-button" onClick={saveProgress} disabled={submitted || maxAttemptsReached}><FileDown size={16} /> Salvar progresso</Button><Button variant="outline" className="pdf-button" onClick={exportPdfReport} disabled={!submitted}><FileDown size={16} /> Exportar PDF</Button></div>
              </div>
              {progressNotice && <p className="text-xs px-8 pb-3 text-[#497464] font-bold">{progressNotice}</p>}
              <div className="area-performance-grid">
                {visibleAreaPerformance.length ? visibleAreaPerformance.map((area) => { const meta = areaMeta[area.area as AreaName]; return <div className="area-performance" key={area.area}><div><span style={{ background: meta.color }}></span><p>{area.short}<small>{area.correct}/25 acertos</small></p><strong>{area.percentage}%</strong></div><div className="performance-track"><i style={{ width: `${area.percentage}%`, background: meta.color }}></i></div><small>{area.answered} respondidas · {area.blank} em branco</small></div>; }) : <div className="area-performance"><div><p>Desempenho por área</p><strong>—</strong></div><div className="performance-track"><i style={{ width: "0%" }}></i></div><small>Disponível após finalizar a tentativa.</small></div>}
              </div>
              {submitted && <div className="result-ready"><Check size={17} /><p><strong>Resultado registrado.</strong> Consulte as respostas comentadas, analise os percentuais por área e exporte seu relatório personalizado.</p>{!maxAttemptsReached && <button onClick={beginNextAttempt}>Iniciar tentativa {attemptsUsed + 1} de {MAX_DAILY_ATTEMPTS} hoje <ArrowRight size={14} /></button>}</div>}
              {todayStudentAttempts.length > 0 && <section className="mt-5 border-t border-[#E0D7CA] pt-5" aria-labelledby="evolucao-aluno">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div><span className="mini-label">EVOLUÇÃO DO DIA</span><h3 id="evolucao-aluno" className="mt-1 text-lg font-extrabold text-[#1D2A44]">Suas tentativas em perspectiva</h3><p className="mt-1 text-sm text-[#657083]">O gráfico considera somente tentativas concluídas hoje. A linha mostra os acertos simples de cada realização.</p></div>
                  <strong className="text-sm text-[#497464]">{todayStudentAttempts.length} de {MAX_DAILY_ATTEMPTS} concluída(s)</strong>
                </div>
                <div className="mt-4 grid gap-4 lg:grid-cols-[1.5fr_1fr]">
                  <div className="h-64 rounded-sm border border-[#D8D0C3] bg-[#FBF8F2] p-3"><ResponsiveContainer width="100%" height="100%"><LineChart data={evolutionChartData} margin={{ top: 12, right: 18, left: -12, bottom: 4 }}><CartesianGrid strokeDasharray="3 3" stroke="#E0D7CA" vertical={false} /><XAxis dataKey="name" tick={{ fill: "#657083", fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} /><YAxis domain={[0, 100]} tick={{ fill: "#657083", fontSize: 10 }} axisLine={false} tickLine={false} width={32} /><Tooltip formatter={(value, name) => [name === "acertos" ? `${value}/100` : `${value}%`, name === "acertos" ? "Acertos" : "Percentual"]} contentStyle={{ borderRadius: 0, border: "1px solid #D9D0C1", boxShadow: "none", fontFamily: "Manrope", fontSize: 12 }} /><Line type="monotone" dataKey="acertos" stroke="#1D4C72" strokeWidth={3} dot={{ r: 5, fill: "#1D4C72", strokeWidth: 0 }} activeDot={{ r: 7 }} /></LineChart></ResponsiveContainer></div>
                  <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1">{todayStudentAttempts.map((attempt, index) => <button type="button" key={attempt.id} onClick={() => { setSelectedReviewAttemptId(attempt.id); setReviewOpen(true); }} className={"flex items-center justify-between border px-4 py-3 text-left transition " + (selectedReviewAttempt?.id === attempt.id ? "border-[#1D4C72] bg-[#EEF3F7]" : "border-[#E0D7CA] bg-white hover:border-[#8A6B2D]")}><div><span className="text-[10px] font-extrabold uppercase tracking-wider text-[#8A6B2D]">Tentativa {index + 1}</span><p className="mt-1 text-xs text-[#657083]">{new Date(attempt.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {attempt.answered}/100 respondidas</p></div><strong className="text-lg text-[#1D2A44]">{attempt.correct}/100</strong></button>)}</div>
                </div>
                {selectedReviewAttempt && <div className="mt-4 border border-[#D8D0C3] bg-white p-4"><div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between"><div><span className="mini-label">REVISÃO DA TENTATIVA</span><h4 className="mt-1 text-base font-extrabold text-[#1D2A44]">T{todayStudentAttempts.findIndex((item) => item.id === selectedReviewAttempt.id) + 1} · {selectedReviewAttempt.correct}/100 acertos</h4><p className="text-xs text-[#657083]">{selectedReviewAttempt.answered}/100 respondidas · {selectedReviewWrongQuestions.length} erro(s)</p></div><button type="button" className="text-xs font-extrabold text-[#1D4C72] underline" onClick={() => setReviewOpen((value) => !value)}>{reviewOpen ? "Ocultar respostas" : "Liberar respostas e explicações"}</button></div>{reviewOpen && <div className="mt-4 grid gap-3">{selectedReviewWrongQuestions.length ? selectedReviewWrongQuestions.map((item) => <article key={item.displayNumber} className="border border-[#E0D7CA] bg-[#FBF8F2] p-4"><p className="text-sm font-bold text-[#1D2A44]">Questão {String(item.displayNumber).padStart(2, "0")}</p><p className="mt-1 text-sm text-[#435064]">{item.question.enunciado}</p><div className="mt-3 flex flex-wrap gap-3 text-xs"><span>Sua resposta: <b>{item.selected}</b></span><span>Resposta correta: <b>{item.question.correta}</b></span></div><p className="mt-2 text-xs leading-5 text-[#657083]"><b>Explicação:</b> {item.question.justificativa}</p></article>) : <p className="text-sm text-[#657083]">Não há respostas incorretas registradas nesta tentativa.</p>}</div>}</div>}
              </section>}
              {todayStudentAttempts.length >= 2 && <section className="mt-5 border-t border-[#E0D7CA] pt-5" aria-labelledby="comparativo-tentativas">
                <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                  <div><span className="mini-label">COMPARATIVO DAS TENTATIVAS</span><h3 id="comparativo-tentativas" className="mt-1 text-lg font-extrabold text-[#1D2A44]">Evolução por tentativa e por área</h3><p className="mt-1 text-sm text-[#657083]">Compare os acertos simples de cada tentativa concluída hoje. O resultado abaixo não representa nota TRI.</p></div>
                  <strong className="text-sm text-[#497464]">{todayStudentAttempts.length} tentativas concluídas</strong>
                </div>
                <div className="mt-4 overflow-x-auto border border-[#D8D0C3] bg-[#FBF8F2]">
                  <table className="w-full min-w-[620px] text-sm">
                    <thead><tr className="border-b border-[#D8D0C3] text-left text-[10px] font-extrabold uppercase tracking-wider text-[#657083]"><th className="px-4 py-3">Área</th>{todayStudentAttempts.map((_, index) => <th key={index} className="px-4 py-3 text-center">T{index + 1}</th>)}</tr></thead>
                    <tbody>
                      {Object.keys(areaMeta).map((area) => {
                        const short = getAreaMeta(area).short;
                        return <tr key={area} className="border-b border-[#E0D7CA] last:border-0"><td className="px-4 py-3 font-bold text-[#1D2A44]">{short}</td>{todayStudentAttempts.map((attempt) => { const item = attempt.byArea.find((entry) => entry.short === short); return <td key={attempt.id} className="px-4 py-3 text-center font-extrabold text-[#1D2A44]">{item ? item.correct + "/25" : "—"} <span className="text-xs font-semibold text-[#657083]">({item?.percentage ?? 0}%)</span></td>; })}</tr>;
                      })}
                      <tr className="bg-white font-extrabold text-[#1D2A44]"><td className="px-4 py-3">Total</td>{todayStudentAttempts.map((attempt) => <td key={attempt.id} className="px-4 py-3 text-center">{attempt.correct}/100 <span className="text-xs font-semibold text-[#657083]">({attempt.percentage}%)</span></td>)}</tr>
                    </tbody>
                  </table>
                </div>
              </section>}
              {maxAttemptsReached && <section className="attempts-complete" id="acompanhamento"><div className="attempts-complete-heading"><div><span className="eyebrow"><span></span> Ciclo diário concluído</span><h3>Três tentativas de hoje, agora em <i>perspectiva.</i></h3><p>{isAuthenticated ? "Seu histórico permanece associado a esta conta e novas três tentativas serão liberadas amanhã." : "Entre em uma conta para manter o histórico disponível em outro dispositivo; amanhã haverá três novas tentativas."}</p></div><div className="complete-lock"><LockKeyhole size={20} /><span>3 / 3</span></div></div><div className="local-insights"><article className="attempt-history"><div className="insight-title"><History size={18} /><div><span>HISTÓRICO DO ESTUDANTE</span><strong>{studentName.trim() || "Estudante local"}</strong></div></div>{studentAttempts.map((attempt, index) => <div className="attempt-row" key={attempt.id}><span>{String(index + 1).padStart(2, "0")}</span><p>{new Date(attempt.createdAt).toLocaleDateString("pt-BR")}<small>{attempt.correct}/100 acertos · {attempt.answered} respondidas</small></p><strong>{attempt.percentage}%</strong></div>)}</article><article className="teacher-panel"><div className="insight-title"><UsersRound size={18} /><div><span>PAINEL DOCENTE LOCAL</span><strong>{classroom.trim() || "Turma local"}</strong></div></div><div className="teacher-metrics"><div><strong>{uniqueStudentsInClass}</strong><span>estudantes</span></div><div><strong>{classAttempts.length}</strong><span>tentativas</span></div><div><strong>{classAverage}%</strong><span>média local</span></div></div><p>Os indicadores agregam registros disponíveis neste dispositivo para a turma atual.</p><button className="csv-export" onClick={exportAllAttemptsCsv}><FileDown size={14} /> Exportar CSV de todas as turmas</button></article><article className="anonymous-ranking"><div className="insight-title"><Medal size={18} /><div><span>RANKING ANôNIMO LOCAL</span><strong>Melhor resultado por participante</strong></div></div><div className="ranking-list">{anonymousRanking.map((entry, index) => <div key={entry.label}><span>{index + 1}</span><p>{entry.label}<small>{entry.attempts} {entry.attempts === 1 ? "tentativa" : "tentativas"}</small></p><strong>{entry.best}%</strong></div>)}</div></article></div><section className="review-panel"><div className="review-heading"><div><span className="eyebrow"><span></span> Modo de revisão</span><h4>Erros que viram <i>próximo passo.</i></h4><p>Após a terceira tentativa de hoje, compare suas respostas incorretas da última realização com o gabarito e a explicação detalhada.</p></div><button onClick={() => setReviewOpen((value) => !value)}>{reviewOpen ? "Ocultar revisão" : `Revisar ${wrongQuestions.length} erros`} <ArrowRight size={14} /></button></div>{reviewOpen && <div className="review-list">{latestAttempt?.answers ? wrongQuestions.map((item) => <article className="review-item" key={item.displayNumber}><p><strong>Questão {String(item.displayNumber).padStart(2, "0")}</strong> · {item.question.enunciado}</p><div><span>Sua resposta: <b>{item.selected}</b></span><span>Correta: <b>{item.question.correta}</b></span></div><aside><Check size={15} /> <strong>Explicação:</strong> {item.question.justificativa}</aside></article>) : <p className="review-empty">As tentativas anteriores não registraram as alternativas. A revisão estará disponível nas próximas tentativas concluídas.</p>}</div>}</section></section>}</>}
              {!accessUnlocked && <StudentAccessGate
  onLogin={() => openAuth("login")}
/>}
            </section>
            {accessUnlocked && !teacherMode && <>
            <div className="filter-panel">
              <div className="filter-icon"><Filter size={18} /></div>
              <div className="area-filters" aria-label="Filtro de áreas"><button className={activeArea === "Todas" ? "active" : ""} onClick={() => setActiveArea("Todas")}>Todas <span>{ATTEMPT_QUESTION_COUNT}</span></button>{attemptAreaSummary.map((entry) => <button key={entry.area} style={{ "--filter-color": getAreaMeta(entry.area).color, "--filter-pale": areaMeta[entry.area as AreaName].pale } as React.CSSProperties} className={activeArea === entry.area ? "active" : ""} onClick={() => setActiveArea(entry.area as AreaName)}>{entry.short} <span>{entry.count}</span></button>)}</div>
              <label className="search-field"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar tema ou habilidade" /></label>
            </div>
            <div className="results-bar"><p><strong>{filteredQuestions.length}</strong> itens encontrados {activeArea !== "Todas" && <>em <strong>{areaMeta[activeArea].short}</strong></>}</p><span>Página {page} de {totalPages}</span></div>
            <div className="questions-stack">
              {currentQuestions.length ? currentQuestions.map((q, index) => <QuestionCard key={q.numero} q={q} displayNumber={activeArea === "Todas" ? q.numero : (page - 1) * pageSize + index + 1} selected={answers[q.numero]} onSelect={(answer) => setAnswer(q.numero, answer)} revealed={revealed.has(q.numero)} onReveal={() => reveal(q.numero)} canReveal={submitted} disabled={submitted || maxAttemptsReached || !studentReadyToAnswer} />) : <div className="empty-state"><Search size={25} /><h3>Nenhum item encontrado</h3><p>Tente outro termo de busca ou selecione todas as áreas.</p></div>}
            </div>
            {filteredQuestions.length > pageSize && <div className="pagination"><button disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}><ChevronLeft size={17} /> Anterior</button><div>{Array.from({ length: totalPages }, (_, index) => <button key={index} className={page === index + 1 ? "current" : ""} onClick={() => setPage(index + 1)}>{index + 1}</button>)}</div><button disabled={page === totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Próxima <ChevronRight size={17} /></button></div>}
            </>}
          </div>
        </section>

        {teacherMode && <section className="correction-section" id="correcao">
          <div className="correction-copy">
            <span className="eyebrow light"><span></span> Correção organizada</span>
            <h2>Do cartão-resposta<br />à <i>próxima aula.</i></h2>
            <p>Escolha a turma, acompanhe os resultados e gere os materiais de aplicação.</p>
            <div className="correction-buttons">
              <Button onClick={() => requestCadernoOutput("print")}><Printer size={16} /> Imprimir caderno</Button>
              <Button variant="outline" className="light-outline" onClick={() => requestCadernoOutput("pdf")}><FileDown size={16} /> Exportar caderno PDF</Button>
              <Button variant="outline" className="light-outline" onClick={() => downloadFile("mascara", attemptPrintQuestions)}><ArrowDownToLine size={16} /> Baixar máscara</Button>
              <Button variant="outline" className="light-outline" onClick={() => downloadFile("gabarito", attemptPrintQuestions)}><BookOpenCheck size={16} /> Baixar gabarito</Button>
            </div>
          </div>
          <div className="correction-card">
            <img src="/enem-correction-detail.svg" alt="Detalhe de uma folha de respostas sendo corrigida" />
            <div className="correction-card-body">
              <div><ClipboardCheck size={21} /><span>CHAVE DOCENTE</span></div>
              <h3>100 respostas<br />em uma única matriz.</h3>
              <button onClick={() => setShowKey((value) => !value)}>{showKey ? "Ocultar chave" : "Consultar chave"} <ArrowRight size={16} /></button>
            </div>
          </div>
          {showKey && <div className="answer-key" aria-live="polite">
            <div className="answer-key-title">
              <div><span className="mini-label">GABARITO RÁPIDO</span><h3>Chave de correção</h3></div>
              <button onClick={() => setShowKey(false)} aria-label="Fechar chave"><X size={17} /></button>
            </div>
            <div className="answer-key-grid">
              {attemptPrintQuestions.map((q) => <div key={q.numero}><span>{String(q.numero).padStart(3, "0")}</span><strong>{q.correta}</strong></div>)}
            </div>
          </div>}
          <div className="answer-key">
            <div className="answer-key-title">
              <div><span className="mini-label">PAINEL DOCENTE</span><h3>Acompanhamento por turma</h3><p className="mt-1 text-xs text-[#657083]">Participação, desempenho, evolução e revisão individual.</p></div>
              <div className="flex flex-wrap gap-2">
                <button className="rounded-sm border border-[#D8D0C3] px-3 py-2 text-xs font-extrabold text-[#1D4C72] disabled:opacity-50" disabled={teacherAttemptsLoading} onClick={() => setTeacherRefreshKey((value) => value + 1)}>{teacherAttemptsLoading ? "Atualizando..." : "Atualizar resultados"}</button>
                <button className="rounded-sm border border-[#1D4C72] px-3 py-2 text-xs font-extrabold text-[#1D4C72]" disabled={!teacherRows.length} onClick={exportTeacherClassPdf}><FileDown size={14} className="mr-1 inline" />PDF da turma</button>
                <select value={teacherFilter} onChange={(event) => { setTeacherFilter(event.target.value); setSelectedTeacherStudentId(null); }}>
                  <option value="todas">Todas as turmas</option>
                  {myClassrooms.filter((item) => item.is_teacher).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  {myClassrooms.length === 0 && teacherClassrooms.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
                  <option value="__independent__">Alunos avulsos</option>
                </select>
                <select value={teacherSort} onChange={(event) => setTeacherSort(event.target.value)}>
                  <option value="score">Maior pontuação</option><option value="date">Mais recente</option>
                </select>
              </div>
            </div>
            <div className="teacher-metrics">
              <div><strong>{teacherStudents.length}</strong><span>alunos vinculados</span></div>
              <div><strong>{teacherRows.length}</strong><span>tentativas</span></div>
              <div><strong>{teacherRows.length ? Math.round(teacherRows.reduce((sum, item) => sum + item.percentage, 0) / teacherRows.length) : 0}%</strong><span>média</span></div>
            </div>
            {teacherStudentsNotice && <p className="text-xs py-3 text-[#C84D3A] font-bold">{teacherStudentsNotice}</p>}
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              <div className="border border-[#D8D0C3] bg-[#FBF8F2] p-4"><span className="mini-label">MELHOR RESULTADO</span><strong className="mt-1 block text-2xl text-[#1D2A44]">{teacherRows.length ? Math.max(...teacherRows.map((row) => row.percentage)) + "%" : "—"}</strong></div>
              <div className="border border-[#D8D0C3] bg-[#FBF8F2] p-4"><span className="mini-label">PARTICIPAÇÃO</span><strong className="mt-1 block text-2xl text-[#1D2A44]">{teacherStudents.length ? Math.round(new Set(teacherRows.map((row) => row.studentKey)).size / teacherStudents.length * 100) + "%" : "0%"}</strong></div>
              <div className="border border-[#D8D0C3] bg-[#FBF8F2] p-4"><span className="mini-label">FAIXA</span><strong className="mt-1 block text-2xl text-[#1D2A44]">{teacherRows.length ? Math.min(...teacherRows.map((row) => row.percentage)) + "–" + Math.max(...teacherRows.map((row) => row.percentage)) + "%" : "—"}</strong></div>
            </div>
            <div className="mt-5 border border-[#D8D0C3] bg-white">
              <div className="border-b border-[#D8D0C3] px-4 py-3"><span className="mini-label">DESEMPENHO DA TURMA</span><h4 className="mt-1 font-extrabold text-[#1D2A44]">Comparação por área</h4></div>
              <div className="grid gap-2 p-4">
                {teacherClassMetrics.map((area) => <div key={area.short} className="grid grid-cols-[100px_1fr_55px] items-center gap-3 text-xs"><strong className="text-[#1D2A44]">{area.short}</strong><div className="h-2 overflow-hidden rounded-full bg-[#E8E2D8]"><div className="h-full rounded-full bg-[#3B709D]" style={{ width: area.percentage + "%" }} /></div><strong className="text-right text-[#1D2A44]">{area.percentage}%</strong></div>)}
              </div>
            </div>
            {teacherStudentsLoading && <p className="py-4 text-sm text-[#657083]">Carregando alunos das turmas...</p>}
            {!teacherStudentsLoading && <div className="mt-5 overflow-x-auto border border-[#D8D0C3]">
              <table className="w-full min-w-[760px] text-sm">
                <thead><tr className="border-b border-[#D8D0C3] bg-[#FBF8F2] text-left text-[10px] font-extrabold uppercase tracking-wider text-[#657083]"><th className="px-4 py-3">Aluno</th><th className="px-4 py-3 text-center">T1</th><th className="px-4 py-3 text-center">T2</th><th className="px-4 py-3 text-center">T3</th><th className="px-4 py-3 text-center">Média</th><th className="px-4 py-3 text-center">Ações</th></tr></thead>
                <tbody>
                  {teacherStudents.map((student) => <tr key={student.student_id} className="border-b border-[#E0D7CA] last:border-0">
                    <td className="px-4 py-3"><button className="text-left font-extrabold text-[#1D4C72] hover:underline" onClick={() => setSelectedTeacherStudentId(student.student_id)}>{student.student_name || "Estudante"}</button><small className="block text-xs text-[#657083]">{student.student_email || "sem e-mail"}</small></td>
                    {[0, 1, 2].map((index) => <td key={index} className="px-4 py-3 text-center font-bold text-[#1D2A44]">{teacherRows.filter((attempt) => attempt.studentKey === student.student_id).sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt)).slice(0, 3)[index]?.percentage != null ? teacherRows.filter((attempt) => attempt.studentKey === student.student_id).sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt)).slice(0, 3)[index].percentage + "%" : "—"}</td>)}
                    <td className="px-4 py-3 text-center font-extrabold text-[#1D2A44]">{student.average_percentage}%</td>
                    <td className="px-4 py-3 text-center"><button className="rounded-sm border border-[#C9C0B2] px-3 py-1 text-xs font-bold text-[#1D4C72]" onClick={() => setSelectedTeacherStudentId(student.student_id)}>Ver detalhes</button></td>
                  </tr>)}
                  {!teacherStudents.length && <tr><td colSpan={6} className="px-4 py-5 text-sm text-[#657083]">{selectedTeacherIndependent ? "Nenhum aluno avulso com resultado ainda." : "Nenhum aluno vinculado a esta turma ainda."}</td></tr>}
                </tbody>
              </table>
            </div>}
            {selectedTeacherStudent && <div className="mt-6 border-2 border-[#1D4C72] bg-[#FBF8F2] p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"><div><span className="mini-label">ALUNO SELECIONADO</span><h4 className="mt-1 text-xl font-extrabold text-[#1D2A44]">{selectedTeacherStudent.student_name || "Estudante"}</h4><p className="text-xs text-[#657083]">{selectedTeacherStudent.student_email || "sem e-mail"}</p></div><div className="flex gap-2"><button className="rounded-sm border border-[#1D4C72] px-3 py-2 text-xs font-bold text-[#1D4C72] disabled:opacity-50" disabled={!selectedTeacherAttempts.length} onClick={exportTeacherStudentPdf}><FileDown size={14} className="mr-1 inline" />Gerar relatório PDF</button><button className="rounded-sm border border-[#C9C0B2] px-3 py-2 text-xs font-bold text-[#657083]" onClick={() => setSelectedTeacherStudentId(null)}>Fechar</button></div></div>
              <p className="mt-4 text-sm text-[#435064]">Use os indicadores da tabela acima para acompanhar as tentativas e o desempenho deste aluno.</p>
            </div>}
          </div>
        </section>}
        <section className="sources-section" id="turmas">
          <div className="sources-title"><span className="eyebrow"><span></span> Turmas</span><h2>Organize o<br /><i>acompanhamento.</i></h2></div>
          <div className="sources-list">
            {teacherMode && <div className="disclaimer"><UsersRound size={17} /><div><p><strong>Área do professor.</strong> Crie uma turma e compartilhe o código com os estudantes.</p><button type="button" onClick={handleCreateClassroom}>Criar turma</button></div></div>}
            {!teacherMode && isAuthenticated && (
              <div className="disclaimer">
                <GraduationCap size={17} />
                <div>
                  <p><strong>Perfil atual.</strong> {studentProfile ? `${studentProfile.label} · ${studentProfile.description}` : "Nenhum perfil selecionado ainda."}</p>
                  {studentProfile?.code && <p className="mt-1 text-xs">Turma vinculada automaticamente: <strong>{classroom || studentProfile.label}</strong>.</p>}
                  <button type="button" onClick={() => scrollToSection("resultado")} className="mt-2">Trocar perfil ou atualizar sua identificação</button>
                </div>
              </div>
            )}
            {!teacherMode && !isAuthenticated && <div className="disclaimer"><LockKeyhole size={17} /><p>Entre com sua conta para participar de uma turma ou usar o perfil Aluno avulso.</p></div>}
          </div>
          {myClassrooms.length > 0 && <div className="answer-key-grid">{myClassrooms.map((item) => <div key={item.id}><span>{item.name} · código {item.code}</span><strong>{item.is_teacher ? "Professor" : "Aluno"}</strong></div>)}</div>}
          {classroomNotice && <p className="text-xs px-8 py-3 text-[#497464] font-bold">{classroomNotice}</p>}
        </section>

        <section className="sources-section" id="fontes">
          <div className="sources-title"><span className="eyebrow"><span></span> Transparência editorial</span><h2>Fontes e<br /><i>delimitação.</i></h2></div>
          <div className="sources-list">
            <a href="https://www.gov.br/inep/pt-br/areas-de-atuacao/avaliacao-e-exames-educacionais/enem/provas-e-gabaritos" target="_blank" rel="noreferrer"><span>[1]</span><div><strong>Provas e Gabaritos do ENEM - Inep</strong><p>Estrutura das quatro provas objetivas e acesso aos cadernos oficiais desde 2009.</p></div><ArrowRight size={17} /></a>
            <a href="https://download.inep.gov.br/download/enem/matriz_referencia.pdf" target="_blank" rel="noreferrer"><span>[2]</span><div><strong>Matriz de Referência do ENEM - Inep</strong><p>Eixos cognitivos, competências e habilidades que orientam a elaboração dos itens.</p></div><ArrowRight size={17} /></a>
            <a href="https://www.gov.br/inep/pt-br/areas-de-atuacao/avaliacao-e-exames-educacionais/enem/provas-e-gabaritos/2024" target="_blank" rel="noreferrer"><span>[3]</span><div><strong>Cadernos e gabaritos de 2024 - Inep</strong><p>Referência recente de organização da aplicação regular em primeiro e segundo dias.</p></div><ArrowRight size={17} /></a>
            <a href="https://www.gov.br/inep/pt-br/areas-de-atuacao/avaliacao-e-exames-educacionais/enem/provas-e-gabaritos/2022" target="_blank" rel="noreferrer"><span>[4]</span><div><strong>Cadernos e gabaritos de 2022 - Inep</strong><p>Referência histórica complementar para a continuidade da estrutura dos cadernos.</p></div><ArrowRight size={17} /></a>
          </div>
          <div className="disclaimer"><Info size={17} /><p><strong>Nota de uso.</strong> Este material não é uma prova oficial do Inep nem reproduz integralmente questões de anos anteriores. é um simulado autoral, com adaptação de quatro alternativas, elaborado para prática pedagógica a partir de referências públicas.</p></div>
        </section>
      </main>

      <footer className="footer"><div className="footer-brand"><img src="/simulado-enem-logo.svg" alt="" /><span><strong>SIMULADO</strong><em>ENEM</em></span></div><p>Preparado para revisão, aplicação e correção em contexto escolar.</p>{!teacherMode && <button className="teacher-developer-access" onClick={() => openAuth("login", "developer")}><LockKeyhole size={13} /> Acesso ao professor desenvolvedor</button>}<a href="#inicio">Voltar ao topo ↑</a></footer>
    </div>
  );
}

// Visual math assets are bundled locally by Vite; avoid external viewer dependencies.